import React, { useCallback, useMemo, useState, useEffect } from "react";
import API from "../../api/api";
import normalizeMediaUrl from "../../utils/media";
import DataTable from "../../admin-panel/components/data/DataTable";
import ModelFormDialog from "../../admin-panel/dynamic/ModelFormDialog";
import { useLocation } from "react-router-dom";
import AgencyPackageCardsPanel from "./components/AgencyPackageCardsPanel";
import { INDIAN_STATES, STATE_DISTRICTS_MAP } from "./AdminFranchiseUsers";

function TextInput({ label, value, onChange, placeholder, style }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 12, color: "#64748b" }}>{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          padding: "10px 12px",
          borderRadius: 8,
          border: "1px solid #e2e8f0",
          outline: "none",
          background: "#fff",
          ...style,
        }}
      />
    </div>
  );
}

function Select({ label, value, onChange, options, style }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 12, color: "#64748b" }}>{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          padding: "10px 12px",
          borderRadius: 8,
          border: "1px solid #e2e8f0",
          outline: "none",
          background: "#fff",
          ...style,
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function formatDateTime(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (_) {
    return String(value);
  }
}

const CONSUMER_COLUMN_FIELDS = {
  basic: [
    "__slno",
    "system_serial_number",
    "full_name",
    "phone",
    "term",
    "current_rank",
    "package_buy_date",
    "__login",
    "is_active",
    "direct_sponsor_list",
    "total_earning",
    "main_wallet",
    "__wallet_audit",
    "self_package_pocket",
    "self_block",
    "royalty_term1",
    "sponsor_display",
    "sponsor_term",
    "__edit_view",
    "__reset",
    "account_active",
    "date_joined",
    "payment_mode",
    "pincode",
    "address",
  ],
  package: [
    "__slno",
    "user_code",
    "full_name",
    "term",
    "self_block",
    "package_buy_date",
    "join_prime_750",
    "spp_months_boxes",
    "rank_upgrade_levels",
    "current_rank",
    "subscription_1",
    "subscription_2",
    "subscription_3",
    "smart_product_package",
    "digital_education",
    "__training_certificate",
    "__gift_card_received",
    "new_tour_package",
    "__team_self_package_count",
    "__team_block_id",
  ],
  wallet: [
    "__slno",
    "user_code",
    "full_name",
    "term",
    "total_bonus",
    "total_earning",
    "self_block",
    "royalty_term1",
    "main_wallet",
    "__wallet_audit",
    "coupon_pocket",
    "self_package_pocket",
    "withdrawal_pocket",
    "redeem_points",
    "__add_money",
    "withdrawal_to_pocket",
  ],
  coupons: [
    "__slno",
    "user_code",
    "full_name",
    "coupon_pocket_breakdown",
    "self_package_pocket_details",
    "__coupon_admin_charges",
    "__withdrawal_admin_charges",
    "__package_admin_charges",
  ],
  rewards: [
    "__slno",
    "user_code",
    "full_name",
    "royalty_term1",
    "total_bonus",
    "__franchisee_reference_reward",
    "__zonal_reward",
    "__direct_sponsor_benefit",
    "__level_income_benefit",
    "__smart_product_pocket",
    "__spp_spin_win",
    "__digital_education_spin_win",
  ],
  team: [
    "__slno",
    "user_code",
    "full_name",
    "term",
    "self_block",
    "direct_sponsor_list",
    "sponsor_term",
    "sponsor_display",
    "__shopping_rebirth_count",
    "__franchisee_rebirth_count",
    "__caption_coupon_rebirth_count",
    "__team_self_package_count",
    "__team_block_id",
  ],
};

const CONSUMER_ONLY_ROLE = "user";
const CONSUMER_ONLY_CATEGORY = "consumer";
const CONSUMER_ALL_COLUMN_FIELDS = Array.from(
  new Set(Object.values(CONSUMER_COLUMN_FIELDS).flat())
);
const CONSUMER_COLUMN_VIEW_OPTIONS = [
  { id: "basic", label: "Basic" },
  { id: "package", label: "Package" },
  { id: "wallet", label: "Wallet" },
  { id: "coupons", label: "Coupons" },
  { id: "rewards", label: "Rewards" },
  { id: "team", label: "Team / Rebirth" },
  { id: "all", label: "All Fields" },
];

const NEEDS_SOURCE_LABEL = "Needs source";
const NEEDS_RULE_LABEL = "Needs rule";

function moneyValue(value) {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : String(value);
}


function SourceBadge({ children = NEEDS_SOURCE_LABEL, title }) {
  return (
    <span
      title={title || children}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "3px 8px",
        borderRadius: 8,
        background: "#f8fafc",
        border: "1px solid #cbd5e1",
        color: "#475569",
        fontSize: 11,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function ValueOrSource({ value, empty = NEEDS_SOURCE_LABEL, title }) {
  if (value === null || value === undefined || value === "") {
    return <SourceBadge title={title}>{empty}</SourceBadge>;
  }
  return <span title={String(value)}>{String(value)}</span>;
}

function DetailRow({ label, value }) {
  const display = value === null || value === undefined || value === "" ? "—" : String(value);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "170px minmax(0, 1fr)", gap: 10, padding: "8px 0", borderBottom: "1px solid #eef2f7" }}>
      <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700 }}>{label}</div>
      <div style={{ color: "#0f172a", fontSize: 13, overflowWrap: "anywhere" }}>{display}</div>
    </div>
  );
}

function UplinePathViewer({ user }) {
  const [uplineChain, setUplineChain] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) return;
    
    let isMounted = true;
    const fetchUplines = async () => {
      setLoading(true);
      setError(null);
      const chain = [];
      let depth = 1;
      
      let currentSponsorUsername = user.sponsor_id || (user.registered_by && user.registered_by.username);
      
      while (currentSponsorUsername && depth <= 15) {
        currentSponsorUsername = currentSponsorUsername.trim();
        if (!currentSponsorUsername) break;

        if (currentSponsorUsername === "9999999999") {
          chain.push({
            level: depth,
            id: 32,
            username: "9999999999",
            full_name: "Company Root",
            sponsor_id: ""
          });
          break;
        }

        try {
          const res = await API.get("/api/admin/users/", {
            params: {
              search: currentSponsorUsername,
              page_size: 10
            }
          });
          
          const data = res?.data;
          const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
          
          const matchedUser = results.find(
            (u) => (u.username || "").toLowerCase() === currentSponsorUsername.toLowerCase()
          );
          
          if (!matchedUser) {
            break;
          }

          chain.push({
            level: depth,
            id: matchedUser.id,
            username: matchedUser.username,
            full_name: matchedUser.full_name,
            sponsor_id: matchedUser.sponsor_id
          });

          currentSponsorUsername = matchedUser.sponsor_id;
          depth++;
        } catch (err) {
          console.error("Error fetching upline:", err);
          break;
        }
      }
      
      if (isMounted) {
        setUplineChain(chain);
        setLoading(false);
      }
    };
    
    fetchUplines();
    return () => {
      isMounted = false;
    };
  }, [user]);

  if (loading) {
    return <div style={{ color: "#64748b", fontSize: 13, padding: "8px 0" }}>Loading upline path...</div>;
  }

  if (error) {
    return <div style={{ color: "#ef4444", fontSize: 13, padding: "8px 0" }}>Error loading upline path.</div>;
  }

  if (uplineChain.length === 0) {
    return <div style={{ color: "#64748b", fontSize: 13, padding: "8px 0" }}>No upline path found.</div>;
  }

  return (
    <div style={{ marginTop: 8, border: "1px solid #e2e8f0", borderRadius: 8, background: "#f8fafc", padding: 12 }}>
      {uplineChain.map((u, idx) => (
        <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: idx < uplineChain.length - 1 ? "1px solid #e2e8f0" : "none" }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#64748b", minWidth: 60 }}>Layer {u.level}:</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{u.username}</span>
          <span style={{ fontSize: 13, color: "#475569" }}>({u.full_name})</span>
          <span style={{ fontSize: 12, color: "#94a3b8", marginLeft: "auto" }}>ID: {u.id}</span>
        </div>
      ))}
    </div>
  );
}

function UserWalletDrawerContent({ user }) {
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("ALL"); // 'ALL' | 'P2P' | 'SELF' | 'WITHDRAWALS'
  const [transactions, setTransactions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [error, setError] = useState("");

  const loadData = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError("");
    try {
      const [txRes, withRes] = await Promise.allSettled([
        API.get("/business/admin/wallet-transactions/", { params: { user_id: user.id } }),
        API.get("/admin/withdrawals/", { params: { search: user.phone || user.username } }),
      ]);

      if (txRes.status === "fulfilled") {
        const list = txRes.value?.data?.results || (Array.isArray(txRes.value?.data) ? txRes.value?.data : []);
        setTransactions(list);
      }
      if (withRes.status === "fulfilled") {
        const list = withRes.value?.data?.results || (Array.isArray(withRes.value?.data) ? withRes.value?.data : []);
        setWithdrawals(list);
      }
    } catch (e) {
      setError("Failed to load user wallet records.");
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.phone, user?.username]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Separate P2P vs Self transfers
  const p2pTransfers = useMemo(() => {
    return transactions.filter((t) => {
      const ty = String(t.type || "").toUpperCase();
      const meta = t.meta || {};
      const cat = String(meta.charge_category || meta.target_wallet || "").toUpperCase();
      return (
        ty.includes("P2P") ||
        cat.includes("P2P") ||
        cat.includes("COUPON") ||
        ty === "COUPON_TRANSFER_OUT" ||
        ty === "ADMIN_CHARGE_P2P_FEE" ||
        Boolean(meta.recipient_phone || meta.recipient_username || meta.to_user_phone)
      );
    });
  }, [transactions]);

  const selfTransfers = useMemo(() => {
    return transactions.filter((t) => {
      const ty = String(t.type || "").toUpperCase();
      const meta = t.meta || {};
      const cat = String(meta.charge_category || meta.target_wallet || "").toUpperCase();
      return (
        ty.includes("SELF") ||
        cat.includes("SELF") ||
        ty === "ADMIN_CHARGE_SELF_TRANSFER_FEE" ||
        ty === "SELF_PACKAGE_PURCHASE" ||
        ty.includes("REBIRTH")
      );
    });
  }, [transactions]);

  const totalP2PVolume = useMemo(() => {
    return p2pTransfers.reduce((acc, t) => acc + Math.abs(Number(t.amount || 0)), 0);
  }, [p2pTransfers]);

  const totalSelfVolume = useMemo(() => {
    return selfTransfers.reduce((acc, t) => acc + Math.abs(Number(t.amount || 0)), 0);
  }, [selfTransfers]);

  const totalWithdrawalVolume = useMemo(() => {
    return withdrawals.reduce((acc, w) => acc + Number(w.amount || 0), 0);
  }, [withdrawals]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* 4-Pocket Summary Balances */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
        <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10, padding: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: "#1e40af", textTransform: "uppercase" }}>Main Wallet</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: "#1d4ed8", marginTop: 4 }}>
            ₹{moneyValue(user?.main_wallet ?? user?.wallet_balance ?? 0)}
          </div>
          <div style={{ fontSize: 11, color: "#60a5fa", marginTop: 2 }}>Primary Earnings</div>
        </div>

        <div style={{ background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 10, padding: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: "#065f46", textTransform: "uppercase" }}>Self Package Pocket</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: "#059669", marginTop: 4 }}>
            ₹{moneyValue(user?.self_package_pocket ?? 0)}
          </div>
          <div style={{ fontSize: 11, color: "#34d399", marginTop: 2 }}>Self Rebirths (₹250 IDs)</div>
        </div>

        <div style={{ background: "#faf5ff", border: "1px solid #e9d5ff", borderRadius: 10, padding: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: "#6b21a8", textTransform: "uppercase" }}>Withdrawal Pocket</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: "#7c3aed", marginTop: 4 }}>
            ₹{moneyValue(user?.withdrawal_pocket ?? user?.total_withdrawn ?? 0)}
          </div>
          <div style={{ fontSize: 11, color: "#a855f7", marginTop: 2 }}>Withdrawable Balance</div>
        </div>

        <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: "#92400e", textTransform: "uppercase" }}>Coupon Pocket</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: "#d97706", marginTop: 4 }}>
            ₹{moneyValue(user?.coupon_pocket ?? 0)}
          </div>
          <div style={{ fontSize: 11, color: "#fbbf24", marginTop: 2 }}>Internal Vouchers</div>
        </div>
      </div>

      {/* Segmented Filter Pills */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", borderBottom: "1px solid #e2e8f0", paddingBottom: 10 }}>
        {[
          { id: "ALL", label: `All Records (${transactions.length + withdrawals.length})` },
          { id: "P2P", label: `🔄 P2P to Members (${p2pTransfers.length})` },
          { id: "SELF", label: `🔁 Self Rebirths (${selfTransfers.length})` },
          { id: "WITHDRAWALS", label: `🏦 Bank Withdrawals (${withdrawals.length})` },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setFilterType(tab.id)}
            style={{
              padding: "6px 12px",
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 800,
              cursor: "pointer",
              border: filterType === tab.id ? "1px solid #2563eb" : "1px solid #cbd5e1",
              background: filterType === tab.id ? "#2563eb" : "#ffffff",
              color: filterType === tab.id ? "#ffffff" : "#475569",
              transition: "all 0.15s",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 30, textAlign: "center", color: "#64748b", fontWeight: 700 }}>
          Loading user wallet & withdrawal ledger...
        </div>
      ) : error ? (
        <div style={{ padding: 12, background: "#fee2e2", color: "#b91c1c", borderRadius: 8, fontSize: 12.5, fontWeight: 600 }}>
          {error}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {/* Section A: Bank Withdrawals List */}
          {(filterType === "ALL" || filterType === "WITHDRAWALS") && (
            <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", padding: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <h4 style={{ margin: 0, fontSize: 13, fontWeight: 900, color: "#0f172a" }}>
                  🏦 Bank Withdrawal Requests ({withdrawals.length})
                </h4>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b" }}>
                  Total: ₹{moneyValue(totalWithdrawalVolume)}
                </span>
              </div>

              {withdrawals.length === 0 ? (
                <div style={{ fontSize: 12, color: "#94a3b8", textAlign: "center", padding: "12px 0" }}>
                  No bank withdrawal requests found for this user.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {withdrawals.map((w, idx) => {
                    const statusStr = String(w.status || "PENDING").toUpperCase();
                    const isApproved = statusStr === "APPROVED" || statusStr === "SUCCESS";
                    const isRejected = statusStr === "REJECTED";
                    return (
                      <div
                        key={w.id || idx}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "10px 12px",
                          borderRadius: 8,
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontWeight: 800, fontSize: 14, color: "#0f172a" }}>
                              ₹{moneyValue(w.amount || 0)}
                            </span>
                            <span
                              style={{
                                fontSize: 10.5,
                                fontWeight: 800,
                                padding: "2px 8px",
                                borderRadius: 6,
                                background: isApproved ? "#dcfce7" : isRejected ? "#fee2e2" : "#fef3c7",
                                color: isApproved ? "#15803d" : isRejected ? "#b91c1c" : "#b45309",
                              }}
                            >
                              {statusStr}
                            </span>
                          </div>
                          <div style={{ fontSize: 11, color: "#64748b", marginTop: 3 }}>
                            TDS: -₹{moneyValue(w.tds_amount || w.tax_deducted || 0)} • Net Payout: ₹{moneyValue(w.net_amount || w.amount || 0)}
                          </div>
                          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                            {formatDateTime(w.created_at || w.date)} • {w.bank_name ? `${w.bank_name} (A/C: ${w.account_number || "—"})` : w.upi_id ? `UPI: ${w.upi_id}` : "Bank Transfer"}
                          </div>
                        </div>

                        {w.payout_ref && (
                          <div style={{ fontSize: 11, color: "#0369a1", fontWeight: 700, textAlign: "right" }}>
                            Ref: {w.payout_ref}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Section B: P2P & Self Transfers List */}
          {(filterType === "ALL" || filterType === "P2P" || filterType === "SELF") && (
            <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", padding: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <h4 style={{ margin: 0, fontSize: 13, fontWeight: 900, color: "#0f172a" }}>
                  {filterType === "P2P" ? "🔄 Member P2P Transfers" : filterType === "SELF" ? "🔁 Self Package Conversions" : "📋 Wallet Transfers & Deductions"}
                </h4>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b" }}>
                  {filterType === "P2P" ? `Total: ₹${moneyValue(totalP2PVolume)}` : filterType === "SELF" ? `Total: ₹${moneyValue(totalSelfVolume)}` : `${transactions.length} records`}
                </span>
              </div>

              {(filterType === "P2P" ? p2pTransfers : filterType === "SELF" ? selfTransfers : transactions).length === 0 ? (
                <div style={{ fontSize: 12, color: "#94a3b8", textAlign: "center", padding: "12px 0" }}>
                  No wallet transfers recorded for this category.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {(filterType === "P2P" ? p2pTransfers : filterType === "SELF" ? selfTransfers : transactions).map((tx, idx) => {
                    const meta = tx.meta || {};
                    const isSelf = String(tx.type || "").includes("SELF") || String(meta.charge_category || "").includes("SELF");
                    const isP2P = String(tx.type || "").includes("P2P") || String(meta.charge_category || "").includes("P2P") || Boolean(meta.recipient_phone);
                    const gross = Number(meta.gross_amount || tx.amount || 0);
                    const fee = Number(meta.admin_service_charge_amount || meta.fee_amount || 0);
                    const net = Number(meta.net_amount || gross - fee);

                    return (
                      <div
                        key={tx.id || idx}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "10px 12px",
                          borderRadius: 8,
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span
                              style={{
                                fontSize: 10.5,
                                fontWeight: 800,
                                padding: "2px 8px",
                                borderRadius: 6,
                                background: isSelf ? "#ecfdf5" : isP2P ? "#eff6ff" : "#f1f5f9",
                                color: isSelf ? "#059669" : isP2P ? "#2563eb" : "#475569",
                                border: `1px solid ${isSelf ? "#a7f3d0" : isP2P ? "#bfdbfe" : "#cbd5e1"}`,
                              }}
                            >
                              {isSelf ? "Self Transfer (Rebirth)" : isP2P ? "P2P Member Voucher" : tx.type || "Transfer"}
                            </span>
                            <span style={{ fontSize: 11, color: "#94a3b8" }}>#{tx.id}</span>
                          </div>

                          <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
                            {meta.recipient_phone ? `To Member: ${meta.recipient_phone}` : isSelf ? "Self Rebirth Sub-Account" : tx.source_type || "Main Wallet"}
                          </div>
                          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                            {formatDateTime(tx.created_at)}
                            {fee > 0 && ` • Fee Collected: ₹${moneyValue(fee)} (${meta.admin_service_charge_percent || 7}%)`}
                          </div>
                        </div>

                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontWeight: 900, fontSize: 14, color: isSelf ? "#059669" : "#0f172a" }}>
                            ₹{moneyValue(Math.abs(gross))}
                          </div>
                          {net > 0 && net !== gross && (
                            <div style={{ fontSize: 11, fontWeight: 700, color: "#16a34a" }}>
                              Net: ₹{moneyValue(net)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function UserProfileDrawer({ open, loading, user, onClose, onEdit, onReset, initialTab = "overview" }) {
  const [drawerTab, setDrawerTab] = useState(initialTab || "overview");
  const [cycleLoading, setCycleLoading] = useState(false);
  const [cycleMsg, setCycleMsg] = useState("");

  useEffect(() => {
    if (initialTab) {
      setDrawerTab(initialTab);
    }
  }, [initialTab, user?.id, open]);
  const [userCycleInfo, setUserCycleInfo] = useState(() => {
    if (!user?.id) return { cycle: "Cycle 1", status: "active", layer_limit: 100000, royalty_limit: 50000, total_limit: 150000 };
    try {
      const raw = localStorage.getItem(`trikonekt_cycle_user_${user.id}`) ||
                  localStorage.getItem(`trikonekt_cycle_user_${user.phone}`);
      if (raw) return JSON.parse(raw);
    } catch (_) {}
    return { cycle: "Cycle 1", status: "active", layer_limit: 100000, royalty_limit: 50000, total_limit: 150000 };
  });

  useEffect(() => {
    if (!user?.id) return;
    try {
      const raw = localStorage.getItem(`trikonekt_cycle_user_${user.id}`) ||
                  localStorage.getItem(`trikonekt_cycle_user_${user.phone}`);
      if (raw) setUserCycleInfo(JSON.parse(raw));
      else setUserCycleInfo({ cycle: "Cycle 1", status: "active", layer_limit: 100000, royalty_limit: 50000, total_limit: 150000 });
    } catch (_) {}
  }, [user?.id, user?.phone]);

  const handleAdminUpdateCycle = async (actionType) => {
    if (!user?.id) return;
    setCycleLoading(true);
    setCycleMsg("");
    try {
      let nextCycle = userCycleInfo?.cycle || "Cycle 1";
      let nextStatus = "active";
      let adminAuth = false;

      if (actionType === "authorize") {
        nextStatus = "authorized";
        adminAuth = true;
      } else if (actionType === "activate") {
        nextCycle = "Cycle 2";
        nextStatus = "active";
        adminAuth = true;
      } else if (actionType === "reset") {
        nextCycle = "Cycle 1";
        nextStatus = "active";
        adminAuth = false;
      }

      const updated = {
        ...userCycleInfo,
        active_cycle: nextCycle,
        cycle: nextCycle,
        layer_limit: 100000,
        royalty_limit: 50000,
        total_limit: 150000,
        status: nextStatus,
        admin_authorized: adminAuth,
        adminAuthorized: adminAuth,
        cycle2_enabled: adminAuth,
        updated_at: new Date().toISOString(),
      };

      if (typeof localStorage !== "undefined") {
        localStorage.setItem(`trikonekt_cycle_user_${user.id}`, JSON.stringify(updated));
        if (user.phone) localStorage.setItem(`trikonekt_cycle_user_${user.phone}`, JSON.stringify(updated));
      }

      setUserCycleInfo(updated);

      try {
        await API.post("/api/admin/validator/cycle-config", {
          userId: user.id,
          userCode: user.user_code || user.phone,
          phone: user.phone,
          role: "CONSUMER",
          cycle: nextCycle,
          baseAmount: 2000,
          status: actionType === "activate" ? "ACTIVE" : actionType === "authorize" ? "AUTHORIZED" : "RESET",
        });
      } catch (_) {}

      setCycleMsg(
        actionType === "authorize"
          ? "✅ Cycle 2 Authorized! User can now enter Cycle 2 in-app."
          : actionType === "activate"
          ? "🚀 Cycle 2 Force Activated! Active cycle updated."
          : "🔄 User reset to Cycle 1."
      );
    } catch (err) {
      setCycleMsg("❌ Failed to update cycle: " + (err?.message || "Unknown error"));
    } finally {
      setCycleLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1300 }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(15, 23, 42, 0.45)" }} />
      <aside
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: "min(680px, 100vw)",
          height: "100%",
          background: "#fff",
          boxShadow: "-16px 0 36px rgba(15, 23, 42, 0.25)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ color: "#0f172a", fontSize: 18, fontWeight: 900 }}>{user?.full_name || user?.username || "User Profile"}</div>
              <span style={{ fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 999, background: "#dcfce7", color: "#166534", border: "1px solid #86efac" }}>
                {user?.account_active ? "Active" : "Inactive"}
              </span>
            </div>
            <div style={{ color: "#64748b", fontSize: 13, marginTop: 2 }}>
              Code: <strong>{user?.user_code || user?.phone || user?.username || "—"}</strong> • Sponsor: <strong>{user?.sponsor_id || user?.sponsor_name || "—"}</strong>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              type="button"
              onClick={async () => {
                if (!user?.id) return;
                const ident = user.phone || user.username || `User #${user.id}`;
                const ok = window.confirm(
                  `⚠️ DANGER: RESET ALL DATA FOR ${ident}?\n\n` +
                  `This will wipe matrix positions, packages, ranks, and wallet balances to ₹0.00, and reset password to 123456.\n\n` +
                  `Proceed?`
                );
                if (!ok) return;
                try {
                  const res = await API.post(`/admin/users/${user.id}/growth-reset/`, {});
                  window.alert(res?.data?.message || `User ${ident} reset successfully.`);
                  onClose();
                  if (onReset) onReset();
                } catch (err) {
                  const msg = err?.response?.data?.detail || err?.message || "Failed to reset user.";
                  window.alert(String(msg));
                }
              }}
              style={{
                borderRadius: 8,
                padding: "7px 12px",
                background: "#fee2e2",
                color: "#b91c1c",
                border: "1px solid #fca5a5",
                cursor: "pointer",
                fontWeight: 700,
                fontSize: 12,
              }}
            >
              Reset Data
            </button>
            <button type="button" onClick={onEdit} style={{ borderRadius: 8, padding: "7px 12px", background: "#0ea5e9", color: "#fff", border: "1px solid #0284c7", cursor: "pointer", fontWeight: 700, fontSize: 12 }}>Edit</button>
            <button type="button" onClick={onClose} style={{ borderRadius: 8, padding: "7px 12px", background: "#fff", color: "#0f172a", border: "1px solid #cbd5e1", cursor: "pointer", fontWeight: 700, fontSize: 12 }}>Close</button>
          </div>
        </div>

        {/* Tab Bar */}
        <div style={{ display: "flex", borderBottom: "1px solid #e2e8f0", background: "#fff", padding: "0 16px" }}>
          <button
            type="button"
            onClick={() => setDrawerTab("overview")}
            style={{
              padding: "12px 16px",
              border: "none",
              background: "none",
              borderBottom: drawerTab === "overview" ? "3px solid #2563eb" : "3px solid transparent",
              color: drawerTab === "overview" ? "#2563eb" : "#64748b",
              fontWeight: 800,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            1. User Financial & Layer Summary (Image 2)
          </button>
          <button
            type="button"
            onClick={() => setDrawerTab("package_status")}
            style={{
              padding: "12px 16px",
              border: "none",
              background: "none",
              borderBottom: drawerTab === "package_status" ? "3px solid #2563eb" : "3px solid transparent",
              color: drawerTab === "package_status" ? "#2563eb" : "#64748b",
              fontWeight: 800,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            2. Package Transaction Status (Image 3)
          </button>
          <button
            type="button"
            onClick={() => setDrawerTab("upline")}
            style={{
              padding: "12px 16px",
              border: "none",
              background: "none",
              borderBottom: drawerTab === "upline" ? "3px solid #2563eb" : "3px solid transparent",
              color: drawerTab === "upline" ? "#2563eb" : "#64748b",
              fontWeight: 800,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            3. Referral Upline
          </button>
          <button
            type="button"
            onClick={() => setDrawerTab("wallet_history")}
            style={{
              padding: "12px 16px",
              border: "none",
              background: "none",
              borderBottom: drawerTab === "wallet_history" ? "3px solid #2563eb" : "3px solid transparent",
              color: drawerTab === "wallet_history" ? "#2563eb" : "#64748b",
              fontWeight: 800,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            4. Wallet & Transfers Audit
          </button>
        </div>

        {/* Body */}
        <div style={{ overflow: "auto", padding: 20, flex: 1, background: "#f8fafc" }}>
          {loading ? (
            <div style={{ color: "#64748b", fontWeight: 700, padding: 20 }}>Loading profile...</div>
          ) : drawerTab === "overview" ? (
            /* Image 2: User Financial & Layer Summary */
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ background: "#fff", padding: 16, borderRadius: 12, border: "1px solid #e2e8f0" }}>
                <h4 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 900, color: "#0f172a" }}>Identity & Hierarchy</h4>
                <DetailRow label="User Code" value={user?.user_code || user?.phone || user?.username} />
                <DetailRow label="User Name" value={user?.full_name || user?.username} />
                <DetailRow label="Sponsor Code" value={user?.sponsor_id || user?.registered_by?.username} />
                <DetailRow label="Sponsor Name" value={user?.sponsor_name || user?.sponsor_display} />
                <DetailRow label="Current Layer" value={user?.commission_level ? `Layer ${user.commission_level}` : user?.current_rank || "Free Tier"} />
                <DetailRow label="Current Direct List" value={`${user?.direct_count ?? 0} active users`} />
              </div>

              <div style={{ background: "#fff", padding: 16, borderRadius: 12, border: "1px solid #e2e8f0" }}>
                <h4 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 900, color: "#0f172a" }}>Bank Details</h4>
                <DetailRow label="Bank Name" value={user?.bank_name || user?.kyc?.bank_name || "—"} />
                <DetailRow label="Bank Account" value={user?.bank_account_number || user?.kyc?.account_number || "—"} />
                <DetailRow label="IFSC Code" value={user?.ifsc_code || user?.kyc?.ifsc_code || "—"} />
                <DetailRow label="KYC Status" value={user?.kyc_verified ? "Verified" : user?.kyc_status || "Pending"} />
                <DetailRow label="User Address" value={user?.address || user?.address_pincode || "—"} />
                <DetailRow label="User Pincode" value={user?.pincode || "—"} />
              </div>

              <div style={{ background: "#fff", padding: 16, borderRadius: 12, border: "1px solid #e2e8f0" }}>
                <h4 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 900, color: "#0f172a" }}>Financial & Payment Balances</h4>
                <DetailRow label="Current Total Earnings" value={`₹${moneyValue(user?.total_earning || 0)}`} />
                <DetailRow label="Current Wallet Balance" value={`₹${moneyValue(user?.wallet_balance || user?.main_wallet || 0)}`} />
                <DetailRow label="Total Withdraw" value={`₹${moneyValue(user?.total_withdrawn || user?.withdrawal_pocket || 0)}`} />
                <DetailRow label="No Of Transactions" value={user?.transaction_count ?? 0} />
                <DetailRow label="Gateway Transaction" value={`₹${moneyValue(user?.gateway_payments_total || 0)}`} />
                <DetailRow label="Company Manual Payment" value={`₹${moneyValue(user?.manual_payments_total || 0)}`} />
                <DetailRow label="Active Payment Mode" value={user?.payment_mode || "Wallet / Online"} />
                <DetailRow label="Approved / Reject Status" value={user?.account_active ? "Approved / Active" : "Pending / Blocked"} />
              </div>

              {/* Consumer Multi-Cycle & Earning Cap Management */}
              <div style={{ background: "#fff", padding: 16, borderRadius: 12, border: "1px solid #e2e8f0" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <h4 style={{ margin: 0, fontSize: 14, fontWeight: 900, color: "#0f172a" }}>
                    ♾️ Consumer Multi-Cycle & Earning Cap Control
                  </h4>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      padding: "2px 8px",
                      borderRadius: 999,
                      background: (userCycleInfo?.cycle === "Cycle 2" || userCycleInfo?.active_cycle === "Cycle 2") ? "#dbeafe" : "#dcfce7",
                      color: (userCycleInfo?.cycle === "Cycle 2" || userCycleInfo?.active_cycle === "Cycle 2") ? "#1e40af" : "#166534",
                      border: "1px solid",
                      borderColor: (userCycleInfo?.cycle === "Cycle 2" || userCycleInfo?.active_cycle === "Cycle 2") ? "#93c5fd" : "#86efac",
                    }}
                  >
                    {String(userCycleInfo?.cycle || userCycleInfo?.active_cycle || "Term 1").replace(/Cycle/gi, "Term")}
                  </span>
                </div>

                <DetailRow label="Active Term" value={String(userCycleInfo?.cycle || userCycleInfo?.active_cycle || "Term 1").replace(/Cycle/gi, "Term")} />
                <DetailRow label="Layer Cap Limit" value={`₹${moneyValue(userCycleInfo?.layer_limit || 100000)}`} />
                <DetailRow label="Royalty Cap Limit" value={`₹${moneyValue(userCycleInfo?.royalty_limit || 50000)}`} />
                <DetailRow label="Total Term Cap" value={`₹${moneyValue(userCycleInfo?.total_limit || 150000)}`} />
                <DetailRow
                  label="Term Earning Status"
                  value={
                    Number(user?.total_earning || 0) >= 150000
                      ? "🏆 ₹1.5L Limit Reached (Earning Stopped)"
                      : `Active (₹${moneyValue(user?.total_earning || 0)} / ₹1,50,000 - ${((Number(user?.total_earning || 0) / 150000) * 100).toFixed(1)}%)`
                  }
                />
                <DetailRow
                  label="Admin Gate Authorization"
                  value={
                    userCycleInfo?.admin_authorized || userCycleInfo?.status === "authorized"
                      ? "🔓 Term 2 Authorized by Admin"
                      : (userCycleInfo?.cycle === "Cycle 2" || userCycleInfo?.active_cycle === "Cycle 2")
                      ? "🚀 Term 2 Active"
                      : "🔒 Locked until ₹1.5L reached or Admin authorized"
                  }
                />

                {cycleMsg && (
                  <div
                    style={{
                      margin: "10px 0",
                      padding: "8px 12px",
                      borderRadius: 8,
                      fontSize: 12,
                      fontWeight: 700,
                      background: cycleMsg.startsWith("❌") ? "#fee2e2" : "#f0fdf4",
                      color: cycleMsg.startsWith("❌") ? "#991b1b" : "#166534",
                      border: `1px solid ${cycleMsg.startsWith("❌") ? "#fca5a5" : "#bbf7d0"}`,
                    }}
                  >
                    {cycleMsg}
                  </div>
                )}

                <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    disabled={cycleLoading}
                    onClick={() => handleAdminUpdateCycle("authorize")}
                    style={{
                      flex: 1,
                      minWidth: 140,
                      padding: "8px 12px",
                      borderRadius: 8,
                      background: "#2563eb",
                      color: "#fff",
                      border: "none",
                      fontWeight: 800,
                      fontSize: 12,
                      cursor: "pointer",
                    }}
                  >
                    🔓 Authorize Term 2
                  </button>
                  <button
                    type="button"
                    disabled={cycleLoading}
                    onClick={() => handleAdminUpdateCycle("activate")}
                    style={{
                      flex: 1,
                      minWidth: 140,
                      padding: "8px 12px",
                      borderRadius: 8,
                      background: "#059669",
                      color: "#fff",
                      border: "none",
                      fontWeight: 800,
                      fontSize: 12,
                      cursor: "pointer",
                    }}
                  >
                    🚀 Force Activate Term 2
                  </button>
                  <button
                    type="button"
                    disabled={cycleLoading}
                    onClick={() => handleAdminUpdateCycle("reset")}
                    style={{
                      padding: "8px 12px",
                      borderRadius: 8,
                      background: "#f1f5f9",
                      color: "#475569",
                      border: "1px solid #cbd5e1",
                      fontWeight: 700,
                      fontSize: 12,
                      cursor: "pointer",
                    }}
                  >
                    🔄 Reset Term 1
                  </button>
                </div>
              </div>
            </div>
          ) : drawerTab === "package_status" ? (
            /* Image 3: User Package Transaction Status */
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ background: "#fff", padding: 16, borderRadius: 12, border: "1px solid #e2e8f0" }}>
                <h4 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 900, color: "#0f172a" }}>Package Activation Timeline</h4>
                <DetailRow label="Joining Date" value={formatDateTime(user?.date_joined)} />
                <DetailRow label="Active Date" value={formatDateTime(user?.package_buy_date || user?.date_joined)} />
                <DetailRow label="User Code" value={user?.user_code || user?.phone || user?.username} />
                <DetailRow label="User Name" value={user?.full_name || user?.username} />
                <DetailRow label="Sponsor Code" value={user?.sponsor_id} />
                <DetailRow label="Sponsor Name" value={user?.sponsor_name || user?.sponsor_display} />
                <DetailRow label="Pincode" value={user?.pincode} />
              </div>

              <div style={{ background: "#fff", padding: 16, borderRadius: 12, border: "1px solid #e2e8f0" }}>
                <h4 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 900, color: "#0f172a" }}>Payment & Coupon Channels</h4>
                <DetailRow label="Manual Payment" value={user?.manual_payment_status || `₹${moneyValue(user?.manual_payments_total || 0)} (Receipt Upload)`} />
                <DetailRow label="Gateway Payment" value={user?.gateway_payment_status || `₹${moneyValue(user?.gateway_payments_total || 0)} (Online Paid)`} />
                <DetailRow label="Self Purchase Coupon" value={`₹${moneyValue(user?.self_package_pocket || 0)} (Self Package Pockets)`} />
                <DetailRow label="Package Purchase Coupon" value={`₹${moneyValue(user?.coupon_pocket || 0)} (Standard Vouchers)`} />
                <DetailRow label="Company Growth Coupon" value={`₹${moneyValue(user?.redeem_points || 0)} (Growth / Franchise Bonus)`} />
              </div>
            </div>
          ) : drawerTab === "wallet_history" ? (
            /* Tab 4: Wallet Transfers & Individual User Withdrawal History */
            <UserWalletDrawerContent user={user} />
          ) : (
            /* Tab 3: Referral Upline Path */
            <div style={{ background: "#fff", padding: 16, borderRadius: 12, border: "1px solid #e2e8f0" }}>
              <h4 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 900, color: "#0f172a" }}>Referral Sponsorship Chain (Upline)</h4>
              <UplinePathViewer user={user} />
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

function DirectsModal({ user, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    let isMounted = true;
    setLoading(true);
    setError(null);

    API.get(`/admin/users/${user.id}/directs/`)
      .then((res) => {
        if (isMounted) setData(res.data);
      })
      .catch((err) => {
        if (isMounted) {
          setError(err?.response?.data?.error || err?.message || "Failed to load direct referrals");
        }
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  if (!user) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(15, 23, 42, 0.65)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: 16,
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          width: "100%",
          maxWidth: 920,
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: "1px solid #e2e8f0",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 22px",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#f8fafc",
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: "#0f172a", display: "flex", alignItems: "center", gap: 8 }}>
              <span>👥</span> Direct Referrals for {user.full_name || user.username}
            </h3>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 4, display: "flex", gap: 12, flexWrap: "wrap" }}>
              <span>User Code: <strong style={{ color: "#0f172a" }}>{user.user_code || user.username}</strong></span>
              <span>Phone: <strong style={{ color: "#0f172a" }}>{user.phone || "—"}</strong></span>
              <span>Total Directs: <strong style={{ color: "#2563eb" }}>{data?.count !== undefined ? data.count : "..."}</strong></span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "#f1f5f9",
              border: "1px solid #cbd5e1",
              borderRadius: 8,
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              fontSize: 16,
              color: "#64748b",
              fontWeight: 700,
            }}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: 20, overflow: "auto", flex: 1 }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "48px 0", color: "#64748b" }}>
              <div style={{ fontSize: 26, marginBottom: 8 }}>⏳</div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Loading direct referral list...</div>
            </div>
          ) : error ? (
            <div style={{ padding: 16, borderRadius: 8, background: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5", fontSize: 13, fontWeight: 600 }}>
              {error}
            </div>
          ) : !data?.directs || data.directs.length === 0 ? (
            <div style={{ textAlign: "center", padding: "48px 0", color: "#64748b" }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>👥</div>
              <div style={{ fontWeight: 800, fontSize: 15, color: "#334155" }}>No Direct Referrals Found</div>
              <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 4 }}>This user has not sponsored any members yet.</div>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, textAlign: "left" }}>
                <thead>
                  <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                    <th style={{ padding: "10px 12px", fontWeight: 700, color: "#475569" }}>#</th>
                    <th style={{ padding: "10px 12px", fontWeight: 700, color: "#475569" }}>User Code</th>
                    <th style={{ padding: "10px 12px", fontWeight: 700, color: "#475569" }}>Member Name</th>
                    <th style={{ padding: "10px 12px", fontWeight: 700, color: "#475569" }}>Phone</th>
                    <th style={{ padding: "10px 12px", fontWeight: 700, color: "#475569" }}>Rank Level</th>
                    <th style={{ padding: "10px 12px", fontWeight: 700, color: "#475569" }}>Academic Term</th>
                    <th style={{ padding: "10px 12px", fontWeight: 700, color: "#475569" }}>Status</th>
                    <th style={{ padding: "10px 12px", fontWeight: 700, color: "#475569" }}>Joined Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.directs.map((d) => (
                    <tr key={d.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "10px 12px", color: "#64748b" }}>{d.sl_no}</td>
                      <td style={{ padding: "10px 12px", fontWeight: 700, color: "#0f172a" }}>{d.user_code}</td>
                      <td style={{ padding: "10px 12px", fontWeight: 600, color: "#1e293b" }}>{d.full_name}</td>
                      <td style={{ padding: "10px 12px", color: "#475569" }}>{d.phone}</td>
                      <td style={{ padding: "10px 12px" }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            padding: "2px 8px",
                            borderRadius: 6,
                            background: "#eff6ff",
                            color: "#1d4ed8",
                            border: "1px solid #bfdbfe",
                            fontWeight: 700,
                            fontSize: 12,
                          }}
                        >
                          🎖️ {d.rank_level} ({d.rank_name})
                        </span>
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "2px 8px",
                            borderRadius: 6,
                            background: "#e0f2fe",
                            color: "#0369a1",
                            border: "1px solid #bae6fd",
                            fontWeight: 800,
                            fontSize: 12,
                          }}
                        >
                          🎓 {d.term}
                        </span>
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <span
                          style={{
                            padding: "2px 8px",
                            borderRadius: 6,
                            fontSize: 11,
                            fontWeight: 700,
                            background: d.account_active ? "#dcfce7" : "#fee2e2",
                            color: d.account_active ? "#15803d" : "#b91c1c",
                          }}
                        >
                          {d.account_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td style={{ padding: "10px 12px", color: "#64748b", fontSize: 12 }}>{d.date_joined}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "12px 20px",
            borderTop: "1px solid #e2e8f0",
            background: "#f8fafc",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "8px 18px",
              borderRadius: 8,
              border: "1px solid #cbd5e1",
              background: "#ffffff",
              color: "#334155",
              fontWeight: 700,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const location = useLocation();

  // Filters applied to server fetch
  const [filters, setFilters] = useState(() => {
    // Admin Users is now the Team Consumer admin table. Franchise/agency flows
    // live in separate admin screens, so this page is hard-locked to consumers.
    let activated = "";
    let account_active = "";
    let is_active = "";
    let rawTerm = "";
    try {
      const qs = location?.search || (typeof window !== "undefined" ? (window.location.search || "") : "");
      const params = new URLSearchParams(qs);
      const rawActivated = (params.get("activated") || "").toLowerCase();
      activated = ["1", "true", "yes", "activated"].includes(rawActivated)
        ? "1"
        : (["0", "false", "no", "inactive", "not_activated", "unactivated", "notactivated"].includes(rawActivated) ? "0" : "");
      const rawAccountActive = (params.get("account_active") || "").toLowerCase();
      account_active = ["1", "true", "yes", "active"].includes(rawAccountActive)
        ? "1"
        : (["0", "false", "no", "inactive"].includes(rawAccountActive) ? "0" : "");
      const rawIsActive = (params.get("is_active") || "").toLowerCase();
      is_active = ["1", "true", "yes", "active", "enabled"].includes(rawIsActive)
        ? "1"
        : (["0", "false", "no", "inactive", "blocked", "disabled"].includes(rawIsActive) ? "0" : "");
      rawTerm = (params.get("term") || "").trim();
    } catch (_) {}
    return {
      role: CONSUMER_ONLY_ROLE,
      phone: "",
      category: CONSUMER_ONLY_CATEGORY,
      pincode: "",
      state: "",
      state_name: "",
      district: "",
      package_tier: "",
      rank_layer: "",
      activation_mode: "",
      directs_range: "",
      kyc: "",
      account_active,
      is_active,
      activated,
      term: rawTerm || "",
    };
  });
  const [density, setDensity] = useState("standard");
  const [view, setView] = useState("consumers");
  const [consumerColumnView, setConsumerColumnView] = useState("basic");
  const [reloadKey, setReloadKey] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const [drawerInitialTab, setDrawerInitialTab] = useState("overview");
  const [tempPw, setTempPw] = useState({});
  const [apiErr, setApiErr] = useState("");
  const [exporting, setExporting] = useState(false);
  // Packages drawer (agency-only)
  const [pkgOpen, setPkgOpen] = useState(false);
  const [pkgUser, setPkgUser] = useState(null);
  const [directsModalUser, setDirectsModalUser] = useState(null);
  // Mobile responsiveness hint
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 640 : false);
  const [segmentCounts, setSegmentCounts] = useState({ all: null, consumers: null, merchants: null, agencies: null, employees: null });
  const [countsLoading, setCountsLoading] = useState(false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // District search aliases mapping for database cities
  const DISTRICT_SEARCH_ALIAS_MAP = useMemo(() => ({
    "Bengaluru Urban": "Bangalore",
    "Bengaluru Rural": "Bangalore Rural",
    "Mysuru": "Mysore",
    "Tumakuru": "Tumkur",
    "Belagavi": "Belgaum",
    "Bagalkote": "Bagalkot",
    "Ballari": "Bellary",
    "Kalaburagi": "Gulbarga",
    "Vijayapura": "Bijapur",
    "Shivamogga": "Shimoga",
    "Chikkamagaluru": "Chikmagalur",
    "Chamrajnagar": "Chamrajnagar",
    "Chamarajanagara": "Chamrajnagar",
  }), []);

  // Cascading State and District options
  const stateOptions = useMemo(() => [
    { value: "", label: "All States" },
    ...INDIAN_STATES.map((s) => ({ value: s.name, label: s.name })),
  ], []);

  const availableDistricts = useMemo(() => {
    if (!filters.state_name) return [];
    return STATE_DISTRICTS_MAP[filters.state_name] || [];
  }, [filters.state_name]);

  const districtOptions = useMemo(() => [
    { value: "", label: filters.state_name ? `All ${filters.state_name} Districts` : "Select State First" },
    ...availableDistricts.map((d) => ({ value: d, label: d })),
  ], [filters.state_name, availableDistricts]);

  const handleStateChange = (stateName) => {
    setFilters((prev) => ({
      ...prev,
      state_name: stateName,
      state: stateName,
      district: "",
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      role: CONSUMER_ONLY_ROLE,
      phone: "",
      category: CONSUMER_ONLY_CATEGORY,
      pincode: "",
      state: "",
      state_name: "",
      district: "",
      package_tier: "",
      rank_layer: "",
      activation_mode: "",
      directs_range: "",
      kyc: "",
      account_active: "",
      is_active: "",
      activated: "",
      term: "",
    });
  };

  // Hide less important columns on small screens for better readability
  const mobileHiddenCols = useMemo(
    () => ({}),
    [isMobile]
  );
  const [colVis, setColVis] = useState({});
  useEffect(() => {
    setColVis(mobileHiddenCols);
  }, [mobileHiddenCols]);


  function setF(key, val) {
    if (key === "role" || key === "category") {
      setFilters((f) => ({ ...f, role: CONSUMER_ONLY_ROLE, category: CONSUMER_ONLY_CATEGORY }));
      return;
    }
    setFilters((f) => ({ ...f, [key]: val, role: CONSUMER_ONLY_ROLE, category: CONSUMER_ONLY_CATEGORY }));
  }

  const buildBaseParamsFromFilters = useCallback((f) => {
    const params = {};
    Object.entries(f || {}).forEach(([k, v]) => {
      if (k === "role" || k === "category" || k === "state_name") return;
      if (v !== null && v !== undefined && String(v).trim() !== "") {
        params[k] = v;
      }
    });
    return params;
  }, []);

  const openEdit = useCallback(async (row) => {
    try {
      if (!row || !row.id) return;
      const res = await API.get(`/admin/users/${row.id}/`);
      const data = res?.data || row;
      setSelected({ id: row.id, ...row, ...data });
      setEditOpen(true);
    } catch {
      setSelected(row);
      setEditOpen(true);
    }
  }, []);

  const openUserProfile = useCallback(async (row, tab = "overview") => {
    if (!row || !row.id) return;
    setDrawerInitialTab(tab || "overview");
    setProfileUser(row);
    setProfileOpen(true);
    setProfileLoading(true);
    try {
      const res = await API.get(`/admin/users/${row.id}/`, {
        params: { profile: 1 },
        timeout: 30000,
        retryAttempts: 1,
        dedupe: "cancelPrevious",
      });
      setProfileUser({ ...row, ...(res?.data || {}) });
    } catch (_) {
      setProfileUser(row);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  const openUserWalletDrawer = useCallback((row) => {
    if (!row || !row.id) return;
    openUserProfile(row, "wallet_history");
  }, [openUserProfile]);

  const openDirectsModal = useCallback((row) => {
    if (!row) return;
    setDirectsModalUser(row);
  }, []);

  // Create-new helpers (pre-fill sensible defaults)
  const openCreate = useCallback((defaults = {}) => {
    setSelected({ ...defaults });
    setEditOpen(true);
  }, []);
  const newConsumer = useCallback(() => {
    try {
      window.open("/auth/register-v2/user", "_blank");
    } catch (_) {}
  }, []);
  const newEmployee = useCallback(() => openCreate({ role: "employee", category: "employee" }), [openCreate]);
  const newMerchant = useCallback(() => openCreate({ role: "user", category: "merchant" }), [openCreate]);
  const newAgency = useCallback(() => {
    try {
      window.open("/auth/register-v2/agency", "_blank");
    } catch (_) {}
  }, []);

  // Dynamic admin edit fields: default fallback + fetch from backend meta
  const DEFAULT_EDIT_FIELDS = [
    { name: "email", type: "EmailField", required: false, label: "Email" },
    { name: "full_name", type: "CharField", required: false, label: "Full Name" },
    { name: "phone", type: "CharField", required: false, label: "Mobile" },
    { name: "pincode", type: "CharField", required: false, label: "Pincode" },
    { name: "country", type: "IntegerField", required: false, label: "Country (ID)" },
    { name: "state", type: "IntegerField", required: false, label: "State (ID)" },
    { name: "city", type: "IntegerField", required: false, label: "District/City (ID)" },
    { name: "role", type: "CharField", required: false, label: "Role" },
    { name: "category", type: "CharField", required: false, label: "Category" },
    { name: "is_active", type: "BooleanField", required: false, label: "Active" },
    { name: "password", type: "PasswordField", required: false, label: "Set New Password" },
  ];
  const [editFields, setEditFields] = useState(DEFAULT_EDIT_FIELDS);

  useEffect(() => {
    let mounted = true;
    // Cache edit-meta locally to prevent repeated fetches and avoid cancellations during dev StrictMode
    const LS_KEY = "admin.users.editMeta.json";
    const LS_TS_KEY = "admin.users.editMeta.ts";
    const TTL = 5 * 60 * 1000; // 5 minutes

    const mapPassword = (arr) =>
      arr.map((f) =>
        f && f.name === "password"
          ? { ...f, type: "PasswordField", required: false, label: f.label || "Set New Password" }
          : f
      );

    const readCached = () => {
      try {
        const ts = parseInt(localStorage.getItem(LS_TS_KEY) || "0", 10);
        if (ts && Date.now() - ts < TTL) {
          const raw = localStorage.getItem(LS_KEY);
          if (raw) {
            const data = JSON.parse(raw);
            if (Array.isArray(data)) return data;
          }
        }
      } catch (_) {}
      return null;
    };

    const writeCached = (fields) => {
      try {
        localStorage.setItem(LS_KEY, JSON.stringify(fields || []));
        localStorage.setItem(LS_TS_KEY, String(Date.now()));
      } catch (_) {}
    };

    // Serve cache first if fresh
    const cached = readCached();
    if (cached && mounted) {
      const mapped = mapPassword(cached);
      setEditFields(mapped.length ? mapped : DEFAULT_EDIT_FIELDS);
      return () => {
        mounted = false;
      };
    }

    // Network load with retry, timeout and dedupe disabled (to avoid "cancelled" due to StrictMode double invoke)
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;

    (async () => {
      try {
        const res = await API.get("/admin/users/edit-meta/", {
          timeout: 30000,
          retryAttempts: 1,
          dedupe: "none",
          signal: controller ? controller.signal : undefined,
          cacheTTL: 300000, // in-memory short cache as additional shield
        });
        const arr = res?.data?.fields;
        if (mounted && Array.isArray(arr) && arr.length) {
          const normalized = mapPassword(arr);
          setEditFields(normalized);
          writeCached(normalized);
        }
      } catch (_) {
        // fallback to default fields
      }
    })();

    return () => {
      mounted = false;
      try {
        controller && controller.abort();
      } catch (_) {}
    };
  }, []);

  // Sync filters from URL, but keep this page consumer-only regardless of query.
  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const rawActivated = (params.get("activated") || "").toLowerCase();
    const normActivated = ["1", "true", "yes", "activated"].includes(rawActivated)
      ? "1"
      : (["0", "false", "no", "inactive", "not_activated", "unactivated", "notactivated"].includes(rawActivated) ? "0" : "");
    const rawAccountActive = (params.get("account_active") || "").toLowerCase();
    const normAccountActive = ["1", "true", "yes", "active"].includes(rawAccountActive)
      ? "1"
      : (["0", "false", "no", "inactive"].includes(rawAccountActive) ? "0" : "");
    const rawIsActive = (params.get("is_active") || "").toLowerCase();
    const normIsActive = ["1", "true", "yes", "active", "enabled"].includes(rawIsActive)
      ? "1"
      : (["0", "false", "no", "inactive", "blocked", "disabled"].includes(rawIsActive) ? "0" : "");
    setFilters((f) => {
      let next = f;
      if ((f.activated || "") !== normActivated) next = { ...next, activated: normActivated };
      if ((f.account_active || "") !== normAccountActive) next = { ...next, account_active: normAccountActive };
      if ((f.is_active || "") !== normIsActive) next = { ...next, is_active: normIsActive };
      if ((f.role || "") !== CONSUMER_ONLY_ROLE) next = { ...next, role: CONSUMER_ONLY_ROLE };
      if ((f.category || "") !== CONSUMER_ONLY_CATEGORY) next = { ...next, category: CONSUMER_ONLY_CATEGORY };
      return next;
    });
  }, [location.search]);


  const roleOptions = useMemo(
    () => [
      { value: "", label: "Any role" },
      { value: "user", label: "User" },
      { value: "agency", label: "Agency" },
      { value: "employee", label: "Employee" },
    ],
    []
  );

  const kycOptions = useMemo(
    () => [
      { value: "", label: "Any KYC" },
      { value: "pending", label: "KYC Pending" },
      { value: "verified", label: "KYC Verified" },
    ],
    []
  );

  const accountStatusOptions = useMemo(
    () => [
      { value: "", label: "Any account status" },
      { value: "1", label: "Active" },
      { value: "0", label: "Inactive" },
    ],
    []
  );

  const accessStatusOptions = useMemo(
    () => [
      { value: "", label: "Any access status" },
      { value: "1", label: "Can login" },
      { value: "0", label: "Blocked" },
    ],
    []
  );

  const categoryOptions = useMemo(
    () => [
      { value: "", label: "Any category" },
      { value: "consumer", label: "consumer" },
      { value: "employee", label: "employee" },
      { value: "merchant", label: "merchant" },
      { value: "agency_state_coordinator", label: "agency_state_coordinator" },
      { value: "agency_state", label: "agency_state" },
      { value: "agency_district_coordinator", label: "agency_district_coordinator" },
      { value: "agency_district", label: "agency_district" },
      { value: "agency_pincode_coordinator", label: "agency_pincode_coordinator" },
      { value: "agency_pincode", label: "agency_pincode" },
      { value: "agency_sub_franchise", label: "agency_sub_franchise" },
    ],
    []
  );

  const packageTierOptions = useMemo(
    () => [
      { value: "", label: "All Packages" },
      { value: "2000", label: "₹2,000 Starter Bundle" },
      { value: "8000", label: "₹8,000 Super Agent" },
      { value: "40000", label: "₹40,000 Promoter DAP" },
      { value: "free", label: "Free Tier / Unactivated" },
    ],
    []
  );

  const rankLayerOptions = useMemo(
    () => [
      { value: "", label: "All Ranks / Layers" },
      { value: "L1", label: "L1 - Field Agent" },
      { value: "L2", label: "L2 - Agent" },
      { value: "L3", label: "L3 - Agent" },
      { value: "L4", label: "L4 - Agent" },
      { value: "L5", label: "L5 - Agent" },
      { value: "L6", label: "L6 - Agent" },
      { value: "L7", label: "L7 - Super Agent (Qualified)" },
      { value: "L8", label: "L8 - Super Agent" },
      { value: "L9", label: "L9 - Super Agent" },
      { value: "L10", label: "L10 - District Agent Promoter" },
      { value: "unranked", label: "Unranked / Free" },
    ],
    []
  );

  const activationModeOptions = useMemo(
    () => [
      { value: "", label: "All Activation Modes" },
      { value: "Coupon Pocket", label: "Coupon Pocket (Voucher)" },
      { value: "Wallet", label: "Main Wallet / Add Money" },
      { value: "Payment Gateway", label: "Payment Gateway (PG / Online)" },
      { value: "Admin", label: "Admin Manual Activation" },
    ],
    []
  );

  const directsRangeOptions = useMemo(
    () => [
      { value: "", label: "All Directs Count" },
      { value: "0", label: "0 Direct Sponsors" },
      { value: "1-4", label: "1 to 4 Direct Sponsors" },
      { value: "5+", label: "5+ Directs (Training Pass Qualified)" },
    ],
    []
  );

  // DataGrid columns (flex + minWidth for responsive)
  const columns = useMemo(
    () => [
      
      {
        field: "id",
        headerName: "ID",
        minWidth: 90,
        width: 90,
      },
      {
        field: "avatar",
        headerName: "Profile",
        minWidth: 80,
        renderCell: (params) => {
          const urlRaw = params?.row?.avatar_url || params?.row?.avatar?.url || params?.row?.avatar || "";
          const src = urlRaw ? normalizeMediaUrl(urlRaw) : "";
          return src ? (
            <img src={src} alt="" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", border: "1px solid #e2e8f0" }} />
          ) : (
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#e2e8f0", display: "inline-block" }} />
          );
        },
      },
      {
        field: "username",
        headerName: "Username",
        minWidth: 160,
        flex: 1,
        renderCell: (params) => {
          const row = params?.row || {};
          const uname = row.username || "";
          return (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <button
                type="button"
                onClick={() => openUserProfile(row)}
                style={{ color: "#0ea5e9", background: "transparent", border: "none", cursor: "pointer", padding: 0 }}
                title="View user profile"
              >
                {uname}
              </button>
            </div>
          );
        },
      },
      { field: "full_name", headerName: "Full Name", minWidth: 180, flex: 1 },
      { field: "sponsor_id", headerName: "Sponsor ID", minWidth: 160 },
      { field: "phone", headerName: "Mobile", minWidth: 140 },
      { field: "email", headerName: "Email", minWidth: 200, flex: 1 },
      {
        field: "commission_percent",
        headerName: "Commission %",
        minWidth: 140,
        valueFormatter: (v) => {
          const n = parseFloat(v);
          return Number.isFinite(n) ? `${n}%` : "";
        },
      },
      {
        field: "service_mode",
        headerName: "Service Mode",
        minWidth: 140,
      },
      {
        field: "__login",
        headerName: "Login",
        width: 92,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params?.row || {};
          const canLogin = row.is_active !== false;
          const onLogin = async (e) => {
            e?.stopPropagation?.();
            try {
              if (!row?.id || !canLogin) return;
              const res = await API.post(`/admin/users/${row.id}/impersonate/`);
              const { access, refresh, role } = res?.data || {};
              if (!access || !refresh) return;
              const r = String(role || row?.role || "").toLowerCase();
              const c = String(row?.category || "").toLowerCase();
              const pickNs = (s) => (s.startsWith("agency") ? "agency" : s.startsWith("employee") ? "employee" : "");
              let ns = pickNs(r) || pickNs(c) || (r === "agency" ? "agency" : r === "employee" ? "employee" : "user");
              const base = ns === "user" ? "" : `/${ns}`;
              const query = new URLSearchParams({
                access,
                refresh,
                ns,
              });
              if (ns === "user") {
                query.set("next", "/user/team-dashboard");
                query.set("login_context", "team");
              }
              const url = `${base}/impersonate?${query.toString()}`;
              window.location.assign(url);
            } catch (_) {}
          };
          return (
            <div style={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
              <button
                type="button"
                onClick={onLogin}
                disabled={!canLogin}
                title={canLogin ? "Login as this user" : "User is blocked. Unblock before login."}
                style={{
                  minWidth: 64,
                  borderRadius: 7,
                  padding: "5px 10px",
                  background: canLogin ? "#2563eb" : "#94a3b8",
                  color: "#fff",
                  border: `1px solid ${canLogin ? "#1d4ed8" : "#64748b"}`,
                  cursor: canLogin ? "pointer" : "not-allowed",
                  fontSize: 12,
                  lineHeight: "16px",
                  fontWeight: 800,
                  whiteSpace: "nowrap",
                  opacity: canLogin ? 1 : 0.78,
                }}
              >
                Login
              </button>
            </div>
          );
        },
      },
      {
        field: "__packages",
        headerName: "Packages",
        minWidth: 130,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params?.row || {};
          const role = String(row.role || "").toLowerCase();
          const cat = String(row.category || "").toLowerCase();
          const isAgency = role === "agency" || cat.startsWith("agency");
          if (!isAgency) return "";
          const onOpen = (e) => {
            e?.stopPropagation?.();
            setPkgUser({ id: row.id, username: row.username, full_name: row.full_name });
            setPkgOpen(true);
          };
          return (
            <button
              type="button"
              onClick={onOpen}
              title="View packages for this agency"
              style={{
                borderRadius: 8,
                padding: "6px 10px",
                background: "#0ea5e9",
                color: "#fff",
                border: "1px solid #0284c7",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              Packages
            </button>
          );
        },
      },
      {
        field: "__commissions",
        headerName: "Commissions",
        minWidth: 140,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params?.row || {};
          const r = String(row.role || "").toLowerCase();
          const c = String(row.category || "").toLowerCase();
          const isAgency = r === "agency" || c.startsWith("agency");
          const isEmployee = r === "employee" || c === "employee";
          if (!isAgency && !isEmployee) return "";
          const onOpen = (e) => {
            e?.stopPropagation?.();
            const roleParam = isAgency ? "agency" : "employee";
            const url = `/admin/commissions/history?recipient=${encodeURIComponent(row.id)}&role=${encodeURIComponent(roleParam)}`;
            window.location.assign(url);
          };
          return (
            <button
              type="button"
              onClick={onOpen}
              title="View commission history"
              style={{
                borderRadius: 8,
                padding: "6px 10px",
                background: "#16a34a",
                color: "#fff",
                border: "1px solid #15803d",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              Commissions
            </button>
          );
        },
      },
      
      {
        field: "activated_ecoupon_count",
        headerName: "E Coupons (Activated)",
        minWidth: 170,
        renderCell: (params) => {
          const n = Number(params?.row?.activated_ecoupon_count ?? 0);
          const label = Number.isFinite(n) ? String(n) : "";
          const active = n > 0;
          return (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: isMobile ? "2px 8px" : "3px 8px",
                borderRadius: 8,
                fontSize: isMobile ? 11 : 12,
                fontWeight: 700,
                lineHeight: 1,
                backgroundColor: active ? "#0ea5e9" : "#f1f5f9",
                border: active ? "1px solid #0284c7" : "1px solid #cbd5e1",
                color: active ? "#ffffff" : "#0f172a",
                minWidth: 40,
              }}
              title={active ? `${n} activated` : "0"}
            >
              {label}
            </div>
          );
        },
      },
      {
        field: "last_promo_package",
        headerName: "Promo Package",
        minWidth: 180,
      },
      {
        field: "__prime150",
        headerName: "Prime 150",
        minWidth: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params?.row || {};
          const n = Number(row.prime150_count ?? 0);
          const label = Number.isFinite(n) ? String(n) : "";
          const active = n > 0;
          const onOpen = (e) => {
            e?.stopPropagation?.();
            if (!row?.id) return;
            const url = `/admin/promo-purchases?user_id=${encodeURIComponent(row.id)}&kind=150&status=APPROVED`;
            try { window.location.assign(url); } catch (_) {}
          };
          return (
            <div
              onClick={onOpen}
              role="button"
              title={active ? `${n} approved purchases` : "0"}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: isMobile ? "2px 8px" : "3px 8px",
                borderRadius: 8,
                fontSize: isMobile ? 11 : 12,
                fontWeight: 700,
                lineHeight: 1,
                backgroundColor: active ? "#0ea5e9" : "#f1f5f9",
                border: active ? "1px solid #0284c7" : "1px solid #cbd5e1",
                color: active ? "#ffffff" : "#0f172a",
                minWidth: 40,
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              {label}
            </div>
          );
        },
      },
      {
        field: "__prime750",
        headerName: "Prime 750",
        minWidth: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params?.row || {};
          const n = Number(row.prime750_count ?? 0);
          const label = Number.isFinite(n) ? String(n) : "";
          const active = n > 0;
          const onOpen = (e) => {
            e?.stopPropagation?.();
            if (!row?.id) return;
            const url = `/admin/promo-purchases?user_id=${encodeURIComponent(row.id)}&kind=750&status=APPROVED`;
            try { window.location.assign(url); } catch (_) {}
          };
          return (
            <div
              onClick={onOpen}
              role="button"
              title={active ? `${n} approved purchases` : "0"}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: isMobile ? "2px 8px" : "3px 8px",
                borderRadius: 8,
                fontSize: isMobile ? 11 : 12,
                fontWeight: 700,
                lineHeight: 1,
                backgroundColor: active ? "#0ea5e9" : "#f1f5f9",
                border: active ? "1px solid #0284c7" : "1px solid #cbd5e1",
                color: active ? "#ffffff" : "#0f172a",
                minWidth: 40,
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              {label}
            </div>
          );
        },
      },
      {
        field: "__monthly759",
        headerName: "Monthly 1000",
        minWidth: 190,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params?.row || {};
          const n = Number(row.monthly_759_count ?? 0); // boxes purchased
          const label = Number.isFinite(n) ? String(n) : "";
          const active = n > 0;
          const pkgNo = row.monthly_current_number ?? null;
          const paid = Number(row.monthly_boxes_paid_current ?? 0);
          const total = Number(row.monthly_total_boxes_current ?? 12);
          const rem = Number.isFinite(Number(row.monthly_boxes_remaining_current))
            ? Number(row.monthly_boxes_remaining_current)
            : Math.max(0, total - paid);
          const subtext = `Months - ${paid}/${total} (remaining ${rem})`;
          const onOpen = (e) => {
            e?.stopPropagation?.();
            if (!row?.id) return;
            const url = `/admin/promo-purchases?user_id=${encodeURIComponent(row.id)}&kind=monthly&status=APPROVED`;
            try { window.location.assign(url); } catch (_) {}
          };
          return (
              <div
                onClick={onOpen}
                role="button"
                title={`${n} boxes purchased — ${subtext}`}
                style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: isMobile ? "2px 8px" : "3px 8px",
                borderRadius: 8,
                fontSize: isMobile ? 11 : 12,
                fontWeight: 700,
                lineHeight: 1,
                backgroundColor: active ? "#0ea5e9" : "#f1f5f9",
                border: active ? "1px solid #0284c7" : "1px solid #cbd5e1",
                color: active ? "#ffffff" : "#0f172a",
                minWidth: 40,
                cursor: "pointer",
                userSelect: "none",
              }}
              >
                {subtext}
              </div>
          );
        },
      },
      {
        field: "kyc_status",
        headerName: "KYC",
        minWidth: 120,
        renderCell: (params) => {
          const row = params?.row || {};
          const verified = !!row.kyc_verified;
          const label = verified ? "Verified" : (row.kyc_status || "Pending");
          const bg = verified ? "#10b981" : "#ffffff"; // green / white
          const color = verified ? "#ffffff" : "#b45309"; // white on green, amber-700 text
          const border = verified ? "1px solid #059669" : "1px solid #f59e0b";
          // Button-like styling for Pending and Verified
          const btnStyles = verified
            ? { backgroundColor: "#10b981", border: "1px solid #059669", color: "#ffffff" }
            : { backgroundColor: "#f59e0b", border: "1px solid #d97706", color: "#ffffff" };
          return (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: isMobile ? "2px 8px" : "3px 8px",
                borderRadius: 8,
                fontSize: isMobile ? 11 : 12,
                fontWeight: 700,
                lineHeight: 1,
                ...btnStyles,
              }}
              title={label}
            >
              {label}
            </div>
          );
        },
      },
      {
        field: "kyc_verified_at",
        headerName: "KYC Verified At",
        minWidth: 170,
        valueFormatter: (v) => {
          if (!v) return "";
          try { return new Date(v).toLocaleString(); } catch (_) { return String(v); }
        },
      },
      { field: "pincode", headerName: "Pincode", minWidth: 110 },
      { field: "area", headerName: "Area", minWidth: 160 },
      { field: "taluk_name", headerName: "Taluk", minWidth: 160 },
      { field: "district_name", headerName: "District", minWidth: 150 },
      { field: "state_name", headerName: "State", minWidth: 150 },
      { field: "country_name", headerName: "Country", minWidth: 150 },
      {
        field: "commission_level",
        headerName: isMobile ? "Layer" : "Commission Layer",
        minWidth: isMobile ? 110 : 150,
        renderCell: (params) => {
          const lvl = Number(params?.row?.commission_level || 0);
          const baseStyle = {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isMobile ? "2px 8px" : "3px 8px",
            borderRadius: 8,
            fontSize: isMobile ? 11 : 12,
            fontWeight: 700,
            lineHeight: 1,
          };
          if (!lvl) {
            return (
              <div
                style={{
                  ...baseStyle,
                  backgroundColor: "#f1f5f9", // slate-100
                  border: "1px solid #cbd5e1", // slate-300
                  color: "#0f172a",
                }}
                title="No layer"
              >
                
              </div>
            );
          }
          return (
            <div
              style={{
                ...baseStyle,
                backgroundColor: "#7c3aed", // violet-600
                border: "1px solid #6d28d9", // violet-700
                color: "#ffffff",
              }}
              title={`Layer L${lvl}`}
            >
              L{lvl}
            </div>
          );
        },
      },
      {
        field: "wallet_balance",
        headerName: "Wallet",
        minWidth: isMobile ? 220 : 300,
        renderCell: (params) => {
          const row = params?.row || {};
          const balNum = Number(row.wallet_balance);
          const bal = Number.isFinite(balNum) ? balNum.toFixed(2) : (row.wallet_balance ?? "");
          const onAdjust = async (action) => {
            try {
              if (!row?.id) return;
              const amtStr = window.prompt(`Enter amount to ${action}:`, "");
              if (amtStr === null) return; // cancelled
              const amt = parseFloat(String(amtStr).trim());
              if (!Number.isFinite(amt) || amt <= 0) {
                window.alert("Amount must be a positive number.");
                return;
              }
              const note = window.prompt("Optional note (will be stored in transaction meta):", "") || "";
              await API.post(`/admin/users/${row.id}/wallet-adjust/`, {
                action,
                amount: amt,
                note,
              });
              setReloadKey((k) => k + 1);
            } catch (e) {
              const msg = e?.response?.data?.detail || e?.message || "Wallet adjust failed";
              window.alert(String(msg));
            }
          };
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span title="Main wallet balance" style={{ minWidth: 64 }}>{bal}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation?.(); onAdjust("credit"); }}
                title="Credit wallet"
                style={{
                  borderRadius: 6,
                  padding: "2px 6px",
                  background: "#16a34a",
                  color: "#fff",
                  border: "1px solid #15803d",
                  cursor: "pointer",
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                + Credit
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation?.(); onAdjust("debit"); }}
                title="Debit wallet"
                style={{
                  borderRadius: 6,
                  padding: "2px 6px",
                  background: "#ef4444",
                  color: "#fff",
                  border: "1px solid #b91c1c",
                  cursor: "pointer",
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                - Debit
              </button>
            </div>
          );
        },
      },
      { field: "wallet_status", headerName: "Wallet Status", minWidth: 140 },
      {
        field: "is_active",
        headerName: "Block User",
        width: 128,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const row = params?.row || {};
          const canLogin = row.is_active !== false;
          const onToggleAccess = async (e) => {
            e?.stopPropagation?.();
            if (!row?.id) return;
            const action = canLogin ? "block" : "unblock";
            const ok = window.confirm(
              canLogin
                ? `Block ${row.username || row.full_name || "this user"}? They will not be able to access consumer, team, franchise, or other user apps.`
                : `Unblock ${row.username || row.full_name || "this user"} and allow login again?`
            );
            if (!ok) return;
            try {
              await API.post(`/admin/users/${row.id}/${canLogin ? "deactivate" : "activate"}/`, {});
              setReloadKey((k) => k + 1);
            } catch (e2) {
              const msg = e2?.response?.data?.detail || e2?.message || `Failed to ${action} user`;
              window.alert(String(msg));
            }
          };

          return (
            <div style={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
              <button
                type="button"
                onClick={onToggleAccess}
                title={canLogin ? "Block this consumer" : "Unblock this consumer"}
                style={{
                  minWidth: 82,
                  borderRadius: 7,
                  padding: "5px 10px",
                  background: canLogin ? "#dc2626" : "#16a34a",
                  color: "#fff",
                  border: `1px solid ${canLogin ? "#b91c1c" : "#15803d"}`,
                  cursor: "pointer",
                  fontSize: 12,
                  lineHeight: "16px",
                  fontWeight: 800,
                  whiteSpace: "nowrap",
                  boxShadow: "none",
                }}
              >
                {canLogin ? "Block" : "Unblock"}
              </button>
            </div>
          );
        },
      },
      {
        field: "account_active",
        headerName: "Account",
        minWidth: 160,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const row = params?.row || {};
          const active = !!row.account_active;
          const r = String(row.role || "").toLowerCase();
          const c = String(row.category || "").toLowerCase();
          const isAgency = r === "agency" || c.startsWith("agency");

          const onToggle = async (e) => {
            e?.stopPropagation?.();
            if (!row?.id) return;
            if (isAgency) {
              // For agencies, activation is driven by package payment.
              // Open Packages panel to record payment instead of manual toggle.
              setPkgUser({ id: row.id, username: row.username, full_name: row.full_name });
              setPkgOpen(true);
              return;
            }
            try {
              await API.patch(`/admin/users/${row.id}/`, { account_active: !active });
              setReloadKey((k) => k + 1);
            } catch (_) {}
          };

          const disabled = isAgency;
          const trackStyle = {
            width: 44,
            height: isMobile ? 18 : 22,
            borderRadius: 999,
            display: "inline-flex",
            alignItems: "center",
            padding: 2,
            backgroundColor: disabled ? (active ? "#6ee7b7" : "#cbd5e1") : (active ? "#16a34a" : "#ef4444"),
            border: disabled ? "1px solid #94a3b8" : (active ? "1px solid #15803d" : "1px solid #b91c1c"),
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.8 : 1,
            transition: "background-color 120ms ease, border-color 120ms ease, opacity 120ms ease",
          };
          const knobStyle = {
            width: isMobile ? 12 : 16,
            height: isMobile ? 12 : 16,
            borderRadius: "50%",
            backgroundColor: "#ffffff",
            transform: active ? `translateX(${isMobile ? 22 : 24}px)` : "translateX(0px)",
            transition: "transform 120ms ease",
          };
          const title = isAgency
            ? (active ? "Active (set by package payment). Click to manage Packages." : "Inactive. Activate via Packages (record payment).")
            : (active ? "Active" : "Inactive");

          return (
            <div style={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
              <div
                role="switch"
                aria-checked={active}
                aria-disabled={disabled}
                tabIndex={0}
                onClick={onToggle}
                onKeyDown={(e) => { if (!disabled && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onToggle(e); } }}
                title={title}
                style={trackStyle}
              >
                <div style={knobStyle} />
              </div>
            </div>
          );
        },
      },
      {
        field: "date_joined",
        headerName: "Joined",
        minWidth: 180,
        valueFormatter: (v) => {
          if (!v) return "";
          try { return new Date(v).toLocaleString(); } catch (_) { return String(v); }
        },
      },
      {
        field: "__edit",
        headerName: "Edit",
        minWidth: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params?.row || {};
          const onEdit = (e) => {
            e?.stopPropagation?.();
            openEdit(row);
          };
          return (
            <button
              type="button"
              onClick={onEdit}
              title="Edit user"
              style={{
                borderRadius: 8,
                padding: "6px 10px",
                background: "#0ea5e9",
                color: "#fff",
                border: "1px solid #0284c7",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              Edit
            </button>
          );
        },
      },
    ],
    [openEdit, openUserProfile, setReloadKey, tempPw, isMobile, setPkgOpen, setPkgUser]
  );

  const consumerSummaryColumns = useMemo(
    () => [
      {
        field: "__slno",
        headerName: "SI No",
        minWidth: 60,
        width: 60,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          let num = null;
          try {
            const api = params?.api;
            if (api) {
              const state = api.state || {};
              const pagination = state.pagination?.paginationModel || { page: 0, pageSize: 25 };
              const page = pagination.page || 0;
              const pageSize = pagination.pageSize || 25;
              const rowIndex = api.getRowIndexRelativeToVisibleRows ? api.getRowIndexRelativeToVisibleRows(params.row.id) : 0;
              if (rowIndex !== null && rowIndex !== undefined && rowIndex >= 0) {
                num = page * pageSize + rowIndex + 1;
              }
            }
          } catch (_) {}
          return (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontWeight: 700, color: "#64748b", fontSize: 12 }}>
                {num ?? params?.row?.__slno ?? "—"}
              </span>
            </div>
          );
        },
      },
      {
        field: "__edit_view",
        headerName: "View",
        minWidth: 75,
        width: 75,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params?.row || {};
          return (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <button
                type="button"
                onClick={(e) => {
                  e?.stopPropagation?.();
                  openUserProfile(row);
                }}
                title="View full user profile"
                style={{
                  width: 58,
                  height: 28,
                  borderRadius: 6,
                  background: "#0f172a",
                  color: "#fff",
                  border: "1px solid #0f172a",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 700,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                  transition: "all 150ms ease",
                }}
              >
                View
              </button>
            </div>
          );
        },
      },
      {
        field: "__reset",
        headerName: "Reset",
        minWidth: 75,
        width: 75,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params?.row || {};
          const onReset = async (e) => {
            e?.stopPropagation?.();
            if (!row?.id) return;
            const ident = row.phone || row.username || `User #${row.id}`;
            const ok = window.confirm(
              `⚠️ DANGER: RESET ALL DATA FOR ${ident}?\n\n` +
              `This will permanently:\n` +
              `• Clear all 5-Matrix and 3-Matrix positions\n` +
              `• Clear all packages and SPP monthly box subscriptions\n` +
              `• Clear all digital LMS rank upgrades\n` +
              `• Reset all wallets & pockets to ₹0.00\n` +
              `• Reset password to default '123456'\n` +
              `• Direct sponsor will be preserved.\n\n` +
              `Are you sure you want to proceed with full reset?`
            );
            if (!ok) return;
            try {
              const res = await API.post(`/admin/users/${row.id}/growth-reset/`, {});
              window.alert(res?.data?.message || `User ${ident} reset successfully.`);
              setReloadKey((k) => k + 1);
            } catch (err) {
              const msg = err?.response?.data?.detail || err?.response?.data?.error || err?.message || "Failed to reset user data.";
              window.alert(String(msg));
            }
          };

          return (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <button
                type="button"
                onClick={onReset}
                title="Reset user matrix, packages, wallets & password to fresh state"
                style={{
                  width: 58,
                  height: 28,
                  borderRadius: 6,
                  background: "#fee2e2",
                  color: "#b91c1c",
                  border: "1px solid #fca5a5",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 700,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                  transition: "all 150ms ease",
                }}
              >
                Reset
              </button>
            </div>
          );
        },
      },
      {
        field: "__login",
        headerName: "Login",
        minWidth: 75,
        width: 75,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params?.row || {};
          const canLogin = row.is_active !== false;
          const onLogin = async (e) => {
            e?.stopPropagation?.();
            try {
              if (!row?.id || !canLogin) return;
              const res = await API.post(`/admin/users/${row.id}/impersonate/`);
              const { access, refresh, role } = res?.data || {};
              if (!access || !refresh) return;
              const r = String(role || row?.role || "").toLowerCase();
              const c = String(row?.category || "").toLowerCase();
              const pickNs = (s) => (s.startsWith("agency") ? "agency" : s.startsWith("employee") ? "employee" : "");
              let ns = pickNs(r) || pickNs(c) || (r === "agency" ? "agency" : r === "employee" ? "employee" : "user");
              const base = ns === "user" ? "" : `/${ns}`;
              const query = new URLSearchParams({
                access,
                refresh,
                ns,
              });
              if (ns === "user") {
                query.set("next", "/user/team-dashboard");
                query.set("login_context", "team");
              }
              const url = `${base}/impersonate?${query.toString()}`;
              window.location.assign(url);
            } catch (_) {}
          };
          return (
            <div style={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
              <button
                type="button"
                onClick={onLogin}
                disabled={!canLogin}
                title={canLogin ? "Login as this user" : "User is blocked. Unblock before login."}
                style={{
                  width: 58,
                  height: 28,
                  borderRadius: 6,
                  background: canLogin ? "#2563eb" : "#94a3b8",
                  color: "#fff",
                  border: `1px solid ${canLogin ? "#1d4ed8" : "#64748b"}`,
                  cursor: canLogin ? "pointer" : "not-allowed",
                  fontSize: 12,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: canLogin ? 1 : 0.78,
                }}
              >
                Login
              </button>
            </div>
          );
        },
      },
      {
        field: "is_active",
        headerName: "Block User",
        minWidth: 85,
        width: 85,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params?.row || {};
          const canLogin = row.is_active !== false;
          const onToggleAccess = async (e) => {
            e?.stopPropagation?.();
            if (!row?.id) return;
            const action = canLogin ? "block" : "unblock";
            const ok = window.confirm(
              canLogin
                ? `Block ${row.username || row.full_name || "this user"}? They will not be able to access consumer, team, franchise, or other user apps.`
                : `Unblock ${row.username || row.full_name || "this user"} and allow login again?`
            );
            if (!ok) return;
            try {
              await API.post(`/admin/users/${row.id}/${canLogin ? "deactivate" : "activate"}/`, {});
              setReloadKey((k) => k + 1);
            } catch (e2) {
              const msg = e2?.response?.data?.detail || e2?.message || `Failed to ${action} user`;
              window.alert(String(msg));
            }
          };

          return (
            <div style={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
              <button
                type="button"
                onClick={onToggleAccess}
                title={canLogin ? "Block this consumer" : "Unblock this consumer"}
                style={{
                  width: 68,
                  height: 28,
                  borderRadius: 6,
                  background: canLogin ? "#dc2626" : "#16a34a",
                  color: "#fff",
                  border: `1px solid ${canLogin ? "#b91c1c" : "#15803d"}`,
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {canLogin ? "Block" : "Unblock"}
              </button>
            </div>
          );
        },
      },
      {
        field: "system_serial_number",
        headerName: "DB Unique ID",
        minWidth: 140,
        width: 140,
        align: "center",
        headerAlign: "center",
        renderCell: (p) => {
          const row = p?.row || {};
          const dbId = row?.id ?? row?.system_serial_number ?? "—";
          const displaySerial = dbId !== "—" ? `#${dbId}` : "—";
          return (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span
                title={`Database Primary Key Unique ID: ${dbId}`}
                style={{
                  minWidth: 54,
                  padding: "4px 8px",
                  height: 28,
                  boxSizing: "border-box",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                  fontWeight: 800,
                  fontSize: 12,
                  color: "#0f172a",
                  background: "#f1f5f9",
                  borderRadius: 6,
                  border: "1px solid #cbd5e1",
                  letterSpacing: "0.5px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {displaySerial}
              </span>
            </div>
          );
        },
        valueGetter: (v, row) => row?.id ?? row?.system_serial_number ?? "",
      },
      {
        field: "user_code",
        headerName: "Consumer ID",
        minWidth: 140,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const row = params?.row || {};
          const code = (row.prefixed_id || row.unique_id || row.user_code || `TR${String(row.id).padStart(4, "0")}`).trim();
          return (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span
                style={{
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                  fontWeight: 700,
                  fontSize: 12,
                  color: "#0284c7",
                  background: "#f0f9ff",
                  padding: "3px 8px",
                  borderRadius: 6,
                  border: "1px solid #bae6fd",
                  whiteSpace: "nowrap",
                }}
              >
                {code}
              </span>
            </div>
          );
        },
      },
      {
        field: "full_name",
        headerName: "Consumer Name",
        minWidth: 150,
        width: 150,
        align: "center",
        headerAlign: "center",
        renderCell: (p) => (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontWeight: 700, color: "#1e293b", fontSize: 13 }}>
              {p?.row?.full_name || p?.row?.username || "—"}
            </span>
          </div>
        ),
      },
      {
        field: "phone",
        headerName: "Mobile",
        minWidth: 125,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const row = params?.row || {};
          return (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontWeight: 600, color: "#334155", fontSize: 12, fontFamily: "ui-monospace, monospace" }}>
                {row.phone || row.username || "—"}
              </span>
            </div>
          );
        },
      },
      {
        field: "sponsor_id",
        headerName: "Sponsor Username",
        minWidth: 145,
        align: "center",
        headerAlign: "center",
        renderCell: (p) => {
          const row = p?.row || {};
          const s = (row?.sponsor_id || row?.sponsor_number || "").trim();
          const u = (row?.username || row?.phone || "").trim();
          if (!s || s === "None" || s === "null" || s === u || row?.id === 1 || u === "9999999999") {
            return (
              <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>None</span>
              </div>
            );
          }
          return (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span
                style={{
                  minWidth: 88,
                  padding: "4px 8px",
                  height: 28,
                  boxSizing: "border-box",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                  fontWeight: 700,
                  fontSize: 12,
                  color: "#475569",
                  background: "#f8fafc",
                  borderRadius: 6,
                  border: "1px solid #cbd5e1",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {s}
              </span>
            </div>
          );
        },
      },
      {
        field: "sponsor_name",
        headerName: "Sponsor Name",
        minWidth: 150,
        align: "center",
        headerAlign: "center",
        renderCell: (p) => {
          const row = p?.row || {};
          const name = (row?.sponsor_name || "").trim();
          const u = (row?.username || row?.phone || "").trim();
          if (!name || name === "None" || name === "null" || name === u || row?.id === 1 || u === "9999999999") {
            return (
              <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>—</span>
              </div>
            );
          }
          return (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontWeight: 600, color: "#1e293b", fontSize: 12 }}>
                {name}
              </span>
            </div>
          );
        },
      },
      {
        field: "account_active",
        headerName: "Status",
        minWidth: 85,
        width: 85,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const active = !!params?.row?.account_active;
          return (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span
                style={{
                  width: 68,
                  height: 28,
                  boxSizing: "border-box",
                  borderRadius: 6,
                  background: active ? "#16a34a" : "#dc2626",
                  color: "#fff",
                  border: `1px solid ${active ? "#15803d" : "#b91c1c"}`,
                  fontSize: 12,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {active ? "Active" : "Inactive"}
              </span>
            </div>
          );
        },
      },
      {
        field: "date_joined",
        headerName: "Joined Date",
        minWidth: 160,
        align: "center",
        headerAlign: "center",
        renderCell: (p) => (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color: "#334155", fontSize: 12 }}>{formatDateTime(p?.row?.date_joined)}</span>
          </div>
        ),
      },
      { field: "pincode", headerName: "Pincode", minWidth: 100, renderCell: (p) => p?.row?.pincode || "—" },
      {
        field: "address",
        headerName: "Address",
        minWidth: 220,
        renderCell: (p) => p?.row?.address || p?.row?.address_pincode || "—",
      },
      {
        field: "payment_mode",
        headerName: "Activation Mode",
        minWidth: 100,
        width: 100,
        align: "center",
        headerAlign: "center",
        renderCell: (p) => {
          const m = p?.row?.payment_mode || p?.row?.mode_of_payment || "Wallet";
          return (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span
                style={{
                  width: 68,
                  height: 28,
                  boxSizing: "border-box",
                  borderRadius: 6,
                  background: "#0284c7",
                  color: "#fff",
                  border: "1px solid #0369a1",
                  fontSize: 12,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {m}
              </span>
            </div>
          );
        },
      },
      {
        field: "direct_count",
        headerName: "Current Direct Sponser (Active)",
        minWidth: 190,
        align: "center",
        headerAlign: "center",
        renderCell: (p) => {
          const c = Number(p?.row?.direct_count ?? p?.row?.active_direct_count ?? 0);
          return <span style={{ fontWeight: 800, color: "#0f172a" }}>{c}</span>;
        },
      },
      { field: "sponsor_name", headerName: "Sponsor Name", minWidth: 160 },
      { field: "sponsor_number", headerName: "Sponsor Number", minWidth: 140 },
      { field: "sponsor_display", headerName: "Sponsor ID & Name", minWidth: 220, flex: 1 },
      { field: "address_pincode", headerName: "Address & Pincode", minWidth: 260, flex: 1 },
      {
        field: "__kyc_profile",
        headerName: "KYC Profile",
        minWidth: 140,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const row = params?.row || {};
          const verified = !!row.kyc_verified;
          const label = verified ? "Verified" : row.kyc_status || "Pending";
          return (
            <div style={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
              <span
                title={row.kyc_verified_at ? `Verified: ${formatDateTime(row.kyc_verified_at)}` : label}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 28,
                  padding: "0 10px",
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 800,
                  background: verified ? "#10b981" : "#f59e0b",
                  color: "#fff",
                  boxSizing: "border-box",
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </span>
            </div>
          );
        },
      },
      {
        field: "package_buy_date",
        headerName: "2K Activation Time",
        minWidth: 210,
        align: "center",
        headerAlign: "center",
        renderCell: (p) => {
          const v = p?.row?.package_buy_date;
          if (!v) {
            return (
              <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ color: "#94a3b8", fontSize: "12px", fontStyle: "italic" }}>
                  Not Activated
                </span>
              </div>
            );
          }
          return (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 28,
                  padding: "0 10px",
                  fontWeight: 700,
                  fontSize: "12px",
                  color: "#0369a1",
                  background: "#e0f2fe",
                  borderRadius: "6px",
                  whiteSpace: "nowrap",
                  border: "1px solid #bae6fd",
                  boxSizing: "border-box",
                  lineHeight: 1,
                }}
              >
                ⚡ {formatDateTime(v)}
              </span>
            </div>
          );
        },
        valueFormatter: (v) => formatDateTime(v),
      },
      { field: "join_prime_750", headerName: "Join Prime 750", minWidth: 140, renderCell: (p) => <ValueOrSource value={p?.row?.join_prime_750} empty="No" /> },
      { field: "spp_months_boxes", headerName: "SSV (Months / Boxes)", minWidth: 160, renderCell: (p) => <ValueOrSource value={p?.row?.spp_months_boxes} empty="0" /> },
      { field: "rank_upgrade_levels", headerName: "Rank Upgrade Layers", minWidth: 220, renderCell: (p) => <ValueOrSource value={p?.row?.rank_upgrade_levels} empty="0" /> },
      {
        field: "current_rank",
        headerName: "Rank Layer",
        minWidth: 120,
        align: "center",
        headerAlign: "center",
        renderCell: (p) => {
          const v = p?.row?.current_rank;
          const pkgDate = p?.row?.package_buy_date;
          const isNotActivated =
            !v ||
            v === "-" ||
            v === "None" ||
            v === "Not activated" ||
            !pkgDate ||
            String(pkgDate).trim().toLowerCase() === "not activated";

          if (isNotActivated) {
            return (
              <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 26,
                    padding: "0 10px",
                    fontSize: "11px",
                    fontWeight: 700,
                    backgroundColor: "#fffbeb",
                    color: "#b45309",
                    border: "1px solid #fde68a",
                    borderRadius: "12px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Not activated
                </span>
              </div>
            );
          }
          // Layer 1 to Layer 6 -> Agent (Sky Blue)
          let bg = "#f0f9ff";
          let color = "#0369a1";
          let border = "#bae6fd";
          if (["L1", "L2", "L3", "L4", "L5", "L6"].includes(v)) {
            bg = "#f0f9ff";
            color = "#0369a1";
            border = "#bae6fd";
          } else if (["L7", "L8", "L9"].includes(v)) {
            // Layer 7 to Layer 9 -> Super Agent (Emerald Green)
            bg = "#f0fdf4";
            color = "#15803d";
            border = "#bbf7d0";
          } else if (v === "L10") {
            // Layer 10 -> District Agent Promoter (Gold / Amber)
            bg = "#fffbeb";
            color = "#b45309";
            border = "#fde68a";
          }
          return (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 28,
                  minWidth: 54,
                  padding: "0 10px",
                  fontSize: "12px",
                  fontWeight: 700,
                  backgroundColor: bg,
                  color: color,
                  borderRadius: "6px",
                  border: `1px solid ${border}`,
                  letterSpacing: "0.5px",
                  fontFamily: "monospace",
                  boxSizing: "border-box",
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                }}
              >
                ⚡ {v}
              </span>
            </div>
          );
        },
      },
      { field: "subscription_1", headerName: "Subscription 1", minWidth: 150, renderCell: (p) => <ValueOrSource value={p?.row?.subscription_1} empty={NEEDS_RULE_LABEL} /> },
      { field: "subscription_2", headerName: "Subscription 2", minWidth: 150, renderCell: (p) => <ValueOrSource value={p?.row?.subscription_2} empty={NEEDS_RULE_LABEL} /> },
      { field: "subscription_3", headerName: "Subscription 3", minWidth: 150, renderCell: (p) => <ValueOrSource value={p?.row?.subscription_3} empty={NEEDS_RULE_LABEL} /> },
      { field: "smart_product_package", headerName: "Smart Product Package", minWidth: 190, renderCell: (p) => <ValueOrSource value={p?.row?.smart_product_package} empty={NEEDS_RULE_LABEL} /> },
      { field: "digital_education", headerName: "Digital Education", minWidth: 170, renderCell: (p) => <ValueOrSource value={p?.row?.digital_education} /> },
      { field: "__training_certificate", headerName: "Certified Training Certificate", minWidth: 230, renderCell: () => <SourceBadge /> },
      { field: "__gift_card_received", headerName: "Package Purchase Gift Card Received", minWidth: 270, renderCell: () => <SourceBadge /> },
      {
        field: "term",
        headerName: "Academic Term",
        minWidth: 125,
        width: 125,
        align: "center",
        headerAlign: "center",
        renderCell: (p) => {
          const t = p?.row?.term || "Term 1";
          return (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 28,
                  padding: "0 10px",
                  borderRadius: 6,
                  background: "#e0f2fe",
                  color: "#0369a1",
                  border: "1px solid #bae6fd",
                  fontWeight: 800,
                  fontSize: 12,
                  boxSizing: "border-box",
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                }}
              >
                🎓 {t}
              </span>
            </div>
          );
        },
      },
      {
        field: "total_bonus",
        headerName: "Total Earning Wallet",
        minWidth: 155,
        width: 155,
        align: "center",
        headerAlign: "center",
        renderCell: (p) => {
          const val = p?.row?.total_bonus !== undefined && p?.row?.total_bonus !== null ? p?.row?.total_bonus : p?.row?.total_earning;
          return (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontWeight: 800, color: "#166534", fontSize: 13 }}>
                ₹{moneyValue(val || 0)}
              </span>
            </div>
          );
        },
      },
      {
        field: "self_block",
        headerName: "Self Block",
        minWidth: 180,
        width: 185,
        align: "center",
        headerAlign: "center",
        renderCell: (p) => {
          const val = p?.row?.self_block;
          if (!val || val === "₹0.00 / 0 IDs") {
            return (
              <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>
              </div>
            );
          }
          return (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 28,
                  padding: "0 10px",
                  borderRadius: 6,
                  background: "#f5f3ff",
                  color: "#6d28d9",
                  border: "1px solid #ddd6fe",
                  fontWeight: 700,
                  fontSize: 12,
                  boxSizing: "border-box",
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                }}
              >
                🔄 {val}
              </span>
            </div>
          );
        },
      },
      {
        field: "royalty_term1",
        headerName: "Royalty (Term 1)",
        minWidth: 135,
        width: 135,
        align: "center",
        headerAlign: "center",
        renderCell: (p) => {
          const val = Number(p?.row?.royalty_term1 || 0);
          return (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontWeight: 700, color: val > 0 ? "#0284c7" : "#94a3b8", fontSize: 13 }}>
                ₹{moneyValue(val)}
              </span>
            </div>
          );
        },
      },
      {
        field: "sponsor_term",
        headerName: "Sponsor Term",
        minWidth: 120,
        width: 125,
        align: "center",
        headerAlign: "center",
        renderCell: (p) => {
          const st = p?.row?.sponsor_term;
          if (!st || st === "—") {
            return (
              <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>
              </div>
            );
          }
          return (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 28,
                  padding: "0 10px",
                  borderRadius: 6,
                  background: "#f8fafc",
                  color: "#334155",
                  border: "1px solid #cbd5e1",
                  fontWeight: 700,
                  fontSize: 12,
                  boxSizing: "border-box",
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {st}
              </span>
            </div>
          );
        },
      },
      {
        field: "sponsor_display",
        headerName: "Sponsor ID & Name",
        minWidth: 180,
        width: 190,
        align: "center",
        headerAlign: "center",
        renderCell: (p) => {
          const base = p?.row?.sponsor_display || p?.row?.sponsor_id || "—";
          const lvl = p?.row?.sponsor_level;
          return (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>{base}</span>
              {lvl && lvl !== "—" ? (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    padding: "1px 6px",
                    borderRadius: 4,
                    background: "#f1f5f9",
                    color: "#475569",
                    border: "1px solid #cbd5e1",
                  }}
                >
                  {lvl}
                </span>
              ) : null}
            </div>
          );
        },
      },
      {
        field: "direct_sponsor_list",
        headerName: "Direct Sponsor List",
        minWidth: 145,
        width: 155,
        align: "center",
        headerAlign: "center",
        renderCell: (p) => {
          const row = p?.row || {};
          const dsl = row?.direct_sponsor_list;
          const countMatch = String(dsl || "").match(/\d+/);
          const count = countMatch ? parseInt(countMatch[0], 10) : (row?.direct_count || 0);
          const hasDirects = count > 0;
          const label = count === 0 ? "0 Directs" : `${count} Direct${count !== 1 ? "s" : ""}`;

          return (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <button
                type="button"
                onClick={(e) => {
                  e?.stopPropagation?.();
                  openDirectsModal(row);
                }}
                title="Click to view full list of direct referrals with Rank Level and Academic Term"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 5,
                  height: 28,
                  padding: "0 10px",
                  borderRadius: 6,
                  border: hasDirects ? "1px solid #93c5fd" : "1px solid #e2e8f0",
                  background: hasDirects ? "#eff6ff" : "#f8fafc",
                  color: hasDirects ? "#1d4ed8" : "#94a3b8",
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: 12,
                  whiteSpace: "nowrap",
                  boxSizing: "border-box",
                  lineHeight: 1,
                  transition: "all 150ms ease",
                }}
              >
                <span>👥</span>
                <span style={{ textDecoration: hasDirects ? "underline" : "none" }}>{label}</span>
                {hasDirects && <span style={{ fontSize: 10, color: "#2563eb" }}>🔍</span>}
              </button>
            </div>
          );
        },
      },
      { field: "new_tour_package", headerName: "New Tour Package", minWidth: 170, renderCell: (p) => <ValueOrSource value={p?.row?.new_tour_package} /> },
      {
        field: "total_earning",
        headerName: "Total Earning Wallet",
        minWidth: 155,
        width: 155,
        align: "center",
        headerAlign: "center",
        renderCell: (p) => {
          const val = p?.row?.total_earning !== undefined && p?.row?.total_earning !== null ? p?.row?.total_earning : (p?.row?.total_bonus || 0);
          return (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontWeight: 800, color: "#166534", fontSize: 13 }}>
                ₹{moneyValue(val || 0)}
              </span>
            </div>
          );
        },
      },
      {
        field: "main_wallet",
        headerName: "Main Wallet",
        minWidth: 140,
        width: 140,
        align: "center",
        headerAlign: "center",
        renderCell: (p) => {
          const val = p?.row?.main_wallet !== undefined && p?.row?.main_wallet !== null ? p?.row?.main_wallet : (p?.row?.wallet_balance || 0);
          return (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <button
                type="button"
                onClick={(e) => {
                  e?.stopPropagation?.();
                  openUserWalletDrawer(p?.row);
                }}
                title="Click to view complete Wallet & Transfers Audit"
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px 8px",
                  borderRadius: 6,
                  fontWeight: 800,
                  color: "#0369a1",
                  fontSize: 13,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  textDecoration: "underline",
                  textDecorationColor: "#bae6fd",
                }}
              >
                ₹{moneyValue(val || 0)}
              </button>
            </div>
          );
        },
      },
      {
        field: "__wallet_audit",
        headerName: "Wallet Audit",
        minWidth: 135,
        width: 135,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params?.row || {};
          return (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <button
                type="button"
                onClick={(e) => {
                  e?.stopPropagation?.();
                  openUserWalletDrawer(row);
                }}
                title="Open user wallet ledger, P2P transfers & withdrawal audit"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 5,
                  height: 28,
                  padding: "0 10px",
                  borderRadius: 6,
                  border: "1px solid #0284c7",
                  background: "#e0f2fe",
                  color: "#0369a1",
                  cursor: "pointer",
                  fontWeight: 800,
                  fontSize: 11,
                  whiteSpace: "nowrap",
                  boxSizing: "border-box",
                  lineHeight: 1,
                  boxShadow: "0 1px 2px rgba(2,132,199,0.1)",
                  transition: "all 150ms ease",
                }}
              >
                <span>💳</span>
                <span>Wallet ↗</span>
              </button>
            </div>
          );
        },
      },
      { field: "coupon_pocket", headerName: "Coupon Pocket", minWidth: 150, renderCell: (p) => moneyValue(p?.row?.coupon_pocket) },
      {
        field: "self_package_pocket",
        headerName: "Self Wallet",
        minWidth: 140,
        width: 140,
        align: "center",
        headerAlign: "center",
        renderCell: (p) => {
          const val = p?.row?.self_package_pocket !== undefined && p?.row?.self_package_pocket !== null ? p?.row?.self_package_pocket : 0;
          return (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontWeight: 800, color: "#7c3aed", fontSize: 13 }}>
                ₹{moneyValue(val || 0)}
              </span>
            </div>
          );
        },
      },
      { field: "withdrawal_pocket", headerName: "Withdrawal Pocket", minWidth: 170, renderCell: (p) => moneyValue(p?.row?.withdrawal_pocket) },
      { field: "redeem_points", headerName: "Redeem Points", minWidth: 150, renderCell: (p) => moneyValue(p?.row?.redeem_points) },
      {
        field: "__add_money",
        headerName: "Add Money",
        minWidth: 140,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params?.row || {};
          const onAdjust = async (e) => {
            e?.stopPropagation?.();
            try {
              const amtStr = window.prompt("Enter amount to credit:", "");
              if (amtStr === null) return;
              const amt = parseFloat(String(amtStr).trim());
              if (!Number.isFinite(amt) || amt <= 0) {
                window.alert("Amount must be a positive number.");
                return;
              }
              await API.post(`/admin/users/${row.id}/wallet-adjust/`, { action: "credit", amount: amt, note: "Admin users Add Money column" });
              setReloadKey((k) => k + 1);
            } catch (e2) {
              window.alert(String(e2?.response?.data?.detail || e2?.message || "Add money failed"));
            }
          };
          return (
            <button
              type="button"
              onClick={onAdjust}
              style={{ borderRadius: 8, padding: "6px 10px", background: "#16a34a", color: "#fff", border: "1px solid #15803d", cursor: "pointer", fontSize: 12, fontWeight: 700 }}
            >
              Add Money
            </button>
          );
        },
      },
      { field: "withdrawal_to_pocket", headerName: "Withdrawal To Pocket", minWidth: 180, renderCell: (p) => moneyValue(p?.row?.withdrawal_to_pocket) },
      { field: "coupon_pocket_breakdown", headerName: "Coupon Pocket Breakdown", minWidth: 230, renderCell: (p) => <ValueOrSource value={p?.row?.coupon_pocket_breakdown} /> },
      { field: "self_package_pocket_details", headerName: "Self Package Pocket Details", minWidth: 230, renderCell: (p) => <ValueOrSource value={p?.row?.self_package_pocket_details} /> },
      { field: "__coupon_admin_charges", headerName: "Coupon Admin Charges", minWidth: 190, renderCell: () => <SourceBadge>Needs charge rule</SourceBadge> },
      { field: "__withdrawal_admin_charges", headerName: "Withdrawal Admin Charges", minWidth: 210, renderCell: () => <SourceBadge>Needs charge rule</SourceBadge> },
      { field: "__direct_sponsor_benefit", headerName: "Direct Sponsor Benefit", minWidth: 210, renderCell: () => <SourceBadge>Needs aggregation</SourceBadge> },
      { field: "__level_income_benefit", headerName: "Layer Income Benefit", minWidth: 190, renderCell: () => <SourceBadge>Needs aggregation</SourceBadge> },
      { field: "__smart_product_pocket", headerName: "Smart Product Pocket", minWidth: 190, renderCell: () => <SourceBadge>Needs aggregation</SourceBadge> },
      { field: "__spp_spin_win", headerName: "SPP Spin & Win", minWidth: 170, renderCell: () => <SourceBadge /> },
      { field: "__digital_education_spin_win", headerName: "Digital Education Prime Package Spin & Win", minWidth: 310, renderCell: () => <SourceBadge /> },
      { field: "__shopping_rebirth_count", headerName: "Shopping Self Re-birth ID Count", minWidth: 250, renderCell: () => <SourceBadge>Needs rule</SourceBadge> },
      { field: "__caption_coupon_rebirth_count", headerName: "Caption/Coupon Self Re-birth ID Count", minWidth: 280, renderCell: () => <SourceBadge>Needs rule</SourceBadge> },
      { field: "__team_self_package_count", headerName: "Community Consumer Self Package ID Count", minWidth: 270, renderCell: () => <SourceBadge>Needs rule</SourceBadge> },
      { field: "__team_block_id", headerName: "Community Consumer Block ID", minWidth: 200, renderCell: () => <SourceBadge>Needs rule</SourceBadge> },
    ],
    [openEdit, openUserProfile, openDirectsModal, setReloadKey, openUserWalletDrawer]
  );

  const tableColumns = useMemo(() => {
    const byField = new Map();
    // Prioritize consumerSummaryColumns so custom overrides take precedence
    [...consumerSummaryColumns, ...columns].forEach((col) => {
      if (!col?.field || byField.has(col.field)) return;
      byField.set(col.field, col);
    });
    const fields =
      consumerColumnView === "all"
        ? CONSUMER_ALL_COLUMN_FIELDS
        : (CONSUMER_COLUMN_FIELDS[consumerColumnView] || CONSUMER_COLUMN_FIELDS.basic);
    return fields.map((field) => byField.get(field)).filter(Boolean);
  }, [columns, consumerSummaryColumns, consumerColumnView]);

  // Kept for older toolbar/menu wiring, but this page is consumer-only.
  const applyView = useCallback((v) => {
    setView("consumers");
    setFilters((f) => ({ ...f, role: CONSUMER_ONLY_ROLE, category: CONSUMER_ONLY_CATEGORY }));
  }, []);

  // Server-side fetcher for DataTable
  const fetcher = useCallback(
    async ({ page, pageSize, search, ordering }) => {
      const params = { page, page_size: pageSize, fast: 0 };
      // merge active filters
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== null && v !== undefined && String(v).trim() !== "") {
          params[k] = v;
        }
      });
      if (filters.district) {
        params.district = DISTRICT_SEARCH_ALIAS_MAP[filters.district] || filters.district;
      }
      if (filters.state_name) {
        params.state = filters.state_name;
      }
      if (search && String(search).trim()) params.search = String(search).trim();
      
      const USER_SORT_FIELD_MAP = {
        system_serial_number: "id",
        __slno: "id",
        user_code: "username",
        username: "username",
        phone: "phone",
        full_name: "full_name",
        sponsor_id: "sponsor_id",
        sponsor_name: "sponsor_id",
        sponsor_number: "sponsor_id",
        sponsor_display: "sponsor_id",
        date_joined: "date_joined",
        account_active: "account_active",
        is_active: "is_active",
        pincode: "pincode",
        address: "address",
        address_pincode: "address",
        package_buy_date: "first_purchase_activated_at",
        email: "email",
        id: "id",
        prefixed_id: "prefixed_id",
        unique_id: "unique_id",
        role: "role",
        category: "category",
      };

      const ALLOWED_DJANGO_ORDER_FIELDS = new Set([
        "id",
        "username",
        "phone",
        "email",
        "full_name",
        "sponsor_id",
        "date_joined",
        "account_active",
        "is_active",
        "pincode",
        "address",
        "first_purchase_activated_at",
        "prefixed_id",
        "unique_id",
        "role",
        "category",
      ]);

      let cleanOrdering = "first_purchase_activated_at";
      if (ordering && typeof ordering === "string") {
        const isDesc = ordering.startsWith("-");
        const rawField = isDesc ? ordering.substring(1) : ordering;
        const mappedField = USER_SORT_FIELD_MAP[rawField] || (ALLOWED_DJANGO_ORDER_FIELDS.has(rawField) ? rawField : "first_purchase_activated_at");
        cleanOrdering = isDesc ? `-${mappedField}` : mappedField;
      }

      params.ordering = cleanOrdering;
      params.role = CONSUMER_ONLY_ROLE;
      params.category = CONSUMER_ONLY_CATEGORY;
      params.consumer_columns = consumerColumnView === "all" ? "all" : consumerColumnView;

      try {

        // Use explicit /api path so the call appears as http://localhost:3000/api/admin/users/?page=... in DevTools
        const res = await API.get("/api/admin/users/", {
          params,
          dedupe: "cancelPrevious",
          timeout: 60000,
          retryAttempts: 1,
          cacheTTL: 8000, // short-lived cache to avoid repeat hits during quick UI changes
        });
        const data = res?.data;
        const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
        const hasNext = !!data?.has_next;
        const hasPrev = !!data?.has_prev;
        // Robust rowCount computation:
        // - If backend provides a numeric count, use it.
        // - If it provides a string count (e.g., "68"), parse it.
        // - If count is null/undefined (fast mode), synthesize using page/pageSize and has_next.
        let count;
        const rawCount = data?.count;
        if (typeof rawCount === "number" && Number.isFinite(rawCount)) {
          count = rawCount;
        } else if (typeof rawCount === "string") {
          const s = rawCount.trim().toLowerCase();
          if (s !== "" && s !== "null" && s !== "none" && s !== "nan") {
            const n = parseInt(rawCount, 10);
            if (Number.isFinite(n)) count = n;
          }
        }
        if (typeof count !== "number") {
          const baseIndex = Math.max(0, (Number(page) - 1) * Number(pageSize));
          // Add +1 when hasNext to ensure MUI enables the next-page control.
          count = baseIndex + results.length + (hasNext ? 1 : 0);
        }
        let filtered = results;

        if (filters.package_tier && String(filters.package_tier).trim()) {
          filtered = filtered.filter((r) => {
            const pkgDate = r.package_buy_date;
            const isAct = pkgDate && String(pkgDate).trim().toLowerCase() !== "not activated";
            const rank = String(r.current_rank || "").toUpperCase();
            if (filters.package_tier === "free") {
              return !isAct;
            }
            if (filters.package_tier === "2000") {
              return isAct;
            }
            if (filters.package_tier === "8000") {
              return ["L7", "L8", "L9"].includes(rank);
            }
            if (filters.package_tier === "40000") {
              return rank === "L10";
            }
            return true;
          });
        }

        if (filters.rank_layer && String(filters.rank_layer).trim()) {
          filtered = filtered.filter((r) => {
            const rank = String(r.current_rank || "").trim().toUpperCase();
            if (filters.rank_layer === "unranked") {
              return !rank || rank === "-" || rank === "NONE" || rank === "NOT ACTIVATED";
            }
            return rank === filters.rank_layer.toUpperCase();
          });
        }

        if (filters.activation_mode && String(filters.activation_mode).trim()) {
          const modeLow = String(filters.activation_mode).toLowerCase();
          filtered = filtered.filter((r) => {
            const m = String(r.payment_mode || r.mode_of_payment || "wallet").toLowerCase();
            return m.includes(modeLow);
          });
        }

        if (filters.directs_range && String(filters.directs_range).trim()) {
          filtered = filtered.filter((r) => {
            const c = Number(r.direct_count ?? r.active_direct_count ?? 0);
            if (filters.directs_range === "0") return c === 0;
            if (filters.directs_range === "1-4") return c >= 1 && c <= 4;
            if (filters.directs_range === "5+") return c >= 5;
            return true;
          });
        }

        const finalCount = (filtered.length !== results.length) ? filtered.length : count;
        setApiErr("");
        return { results: filtered, count: finalCount };
      } catch (e) {
        const isCanceled =
          e?.__canceled === true ||
          e?.code === "ERR_CANCELED" ||
          e?.name === "CanceledError" ||
          (typeof e?.message === "string" && /aborted|abort|canceled|cancelled/i.test(e.message));
        if (isCanceled) {
          // Propagate canceled so DataTable ignores and preserves previous rows
          throw e;
        }
        const status = e?.response?.status;
        const msg = e?.response?.data?.detail || e?.message || "Request failed";
        setApiErr(`${status || ""} ${msg}`.trim());
        return { results: [], count: 0 };
      }
    },
    [filters, reloadKey, consumerColumnView]
  );

  const handleExport = async () => {
    setExporting(true);
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== null && v !== undefined && String(v).trim() !== "") {
        params[k] = v;
      }
    });
    params.role = CONSUMER_ONLY_ROLE;
    params.category = CONSUMER_ONLY_CATEGORY;
    params.consumer_columns = "all";

    // Try backend export endpoint first (if available), otherwise fall back to client-side CSV export
    try {
      const res = await API.get("/api/admin/users/export-xlsx", {
        params,
        responseType: "blob",
        timeout: 120000,
        dedupe: "cancelPrevious",
        retryAttempts: 0,
      });
      const blob = new Blob([res?.data || res], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `admin-users-${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      return;
    } catch (e) {
      // Fall back to client-side Excel (.xls) export by paging through the list API
      try {
        const pageSize = 500;
        let page = 1;
        let all = [];
        let total = null;

        // Fetch all pages with current filters (no table search term available here)
        while (true) {
          const pageParams = { ...params, page, page_size: pageSize };
          const resp = await API.get("/api/admin/users/", {
            params: pageParams,
            dedupe: "none",
            retryAttempts: 1,
            timeout: 30000,
          });
          const data = resp?.data;
          const results = Array.isArray(data?.results)
            ? data.results
            : Array.isArray(data)
            ? data
            : [];
let countRaw = data?.count;
let countNum = Number(countRaw);
const count = Number.isFinite(countNum) ? countNum : results.length;
          if (total == null) total = count;
          if (!results.length) break;
          all = all.concat(results);
          if (all.length >= total) break;
          page += 1;
        }

        if (!all.length) {
          window.alert("No data to export for current filters.");
          return;
        }

        // Build Excel-compatible HTML table (.xls)
        const cols = [
          ["ID", (r) => r.id ?? r.pk ?? ""],
          ["Username", (r) => r.username ?? ""],
          ["Full Name", (r) => r.full_name ?? ""],
          ["Phone", (r) => r.phone ?? ""],
          ["Email", (r) => r.email ?? ""],
          ["Sponsor Name", (r) => r.sponsor_name ?? ""],
          ["Sponsor Number", (r) => r.sponsor_number ?? r.sponsor_id ?? ""],
          ["Sponsor ID & Name", (r) => r.sponsor_display ?? ""],
          ["Join Prime 750", (r) => r.join_prime_750 ?? (r.prime750_count > 0 ? `Yes (${r.prime750_count})` : "No")],
          ["Join Prime 750 Count", (r) => r.prime750_count ?? 0],
          ["SPP Months / Boxes", (r) => r.spp_months_boxes ?? r.monthly_759_count ?? 0],
          ["Rank Upgrade Layers Purchased", (r) => r.rank_upgrade_levels ?? "0"],
          ["Rank Upgrade Count", (r) => r.rank_upgrade_count ?? 0],
          ["Current Rank", (r) => r.current_rank ?? ""],
          ["KYC Status", (r) => (r.kyc_verified ? "Verified" : (r.kyc_status || "Pending"))],
          ["KYC Verified At", (r) => r.kyc_verified_at ?? ""],
          ["Package Buy Date", (r) => r.package_buy_date ?? ""],
          ["Subscription 1", (r) => r.subscription_1 ?? ""],
          ["Subscription 2", (r) => r.subscription_2 ?? ""],
          ["Subscription 3", (r) => r.subscription_3 ?? ""],
          ["Smart Product Package", (r) => r.smart_product_package ?? ""],
          ["Digital Education", (r) => r.digital_education ?? ""],
          ["Address & Pincode", (r) => r.address_pincode ?? r.pincode ?? ""],
          ["Pincode", (r) => r.pincode ?? ""],
          ["Area", (r) => r.area ?? ""],
          ["Taluk", (r) => r.taluk_name ?? ""],
          ["District", (r) => r.district_name ?? ""],
          ["State", (r) => r.state_name ?? ""],
          ["Country", (r) => r.country_name ?? ""],
          ["Total Earning", (r) => r.total_earning ?? ""],
          ["Main Wallet", (r) => r.main_wallet ?? ""],
          ["Coupon Pocket", (r) => r.coupon_pocket ?? ""],
          ["Self Package Pocket", (r) => r.self_package_pocket ?? ""],
          ["Withdrawal Pocket", (r) => r.withdrawal_pocket ?? ""],
          ["Redeem Points", (r) => r.redeem_points ?? ""],
          ["Role", (r) => r.role ?? ""],
          ["Category", (r) => r.category ?? ""],
          ["Wallet Balance", (r) => r.wallet_balance ?? ""],
          ["Wallet Status", (r) => r.wallet_status ?? ""],
          ["Account Active", (r) => (r.account_active ? "Active" : "Inactive")],
          ["Date Joined", (r) => r.date_joined ?? ""],
        ];

        const escapeHtml = (val) => {
          if (val === null || val === undefined) return "";
          return String(val)
            .replace(/&/g, "&")
            .replace(/</g, "<")
            .replace(/>/g, ">");
        };

        const headerCells = cols
          .map((c) => `<th style="border:1px solid #999;padding:4px;background:#eef2ff">${escapeHtml(c[0])}</th>`)
          .join("");
        const rowsHtml = all
          .map((r) => {
            const cells = cols
              .map(([, getter]) => `<td style="border:1px solid #999;padding:4px">${escapeHtml(getter(r))}</td>`)
              .join("");
            return `<tr>${cells}</tr>`;
          })
          .join("");

        const html = `<!DOCTYPE html>
<html>
<head><meta charset="UTF-8"></head>
<body>
  <table border="1" cellspacing="0" cellpadding="0">
    <thead><tr>${headerCells}</tr></thead>
    <tbody>${rowsHtml}</tbody>
  </table>
</body>
</html>`;

        const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `admin-users-${ts}.xls`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        return;
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || "Export failed";
        window.alert(String(msg));
      }
    } finally {
      setExporting(false);
    }
  };

  // Lightweight segment counts — single aggregated call (avoid 5 onload requests)
  useEffect(() => {
    let mounted = true;
    setCountsLoading(true);
    (async () => {
      try {
        const res = await API.get("admin/users/category-counts/", {
          timeout: 15000,
          retryAttempts: 0,
          dedupe: "cancelPrevious",
        });
        const c = res?.data || {};
        const num = (v) => {
          const n = Number(v);
          return Number.isFinite(n) ? n : 0;
        };
        const agencies =
          num(c.agency_state_coordinator) +
          num(c.agency_state) +
          num(c.agency_district_coordinator) +
          num(c.agency_district) +
          num(c.agency_pincode_coordinator) +
          num(c.agency_pincode) +
          num(c.agency_sub_franchise);
        const merchants = num(c.business) + num(c.merchant) + num(c.role_business);
        const all = Number.isFinite(Number(c.all)) ? Number(c.all) : (Number.isFinite(Number(c.total)) ? Number(c.total) : null);
        if (mounted) {
          setSegmentCounts({
            all,
            consumers: Number.isFinite(Number(c.consumer)) ? Number(c.consumer) : null,
            merchants: merchants || null,
            agencies: agencies || null,
            employees: Number.isFinite(Number(c.employee)) ? Number(c.employee) : null,
          });
        }
      } catch (_) {
        if (mounted) {
          // Keep UI responsive; counts are optional
          setSegmentCounts({ all: null, consumers: null, merchants: null, agencies: null, employees: null });
        }
      } finally {
        if (mounted) setCountsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [reloadKey]);

  const toolbar = (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginRight: 8 }}>
        <span
          title="This page is locked to role=user and category=consumer."
          style={{
            padding: "6px 10px",
            borderRadius: 8,
            border: "1px solid #bbf7d0",
            background: "#dcfce7",
            color: "#166534",
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          Consumers{segmentCounts?.consumers != null ? ` (${segmentCounts.consumers})` : ""}
        </span>
      </div>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <label style={{ fontSize: 12, color: "#64748b" }}>Columns</label>
        {CONSUMER_COLUMN_VIEW_OPTIONS.map((v) => (
          <button
            key={v.id}
            onClick={() => setConsumerColumnView(v.id)}
            aria-pressed={consumerColumnView === v.id}
            style={{
              padding: "6px 10px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              background: consumerColumnView === v.id ? "#0f172a" : "#fff",
              color: consumerColumnView === v.id ? "#fff" : "#0f172a",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {v.label}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <label style={{ fontSize: 12, color: "#64748b" }}>Density</label>
        <div style={{ display: "inline-flex", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
          <button
            onClick={() => setDensity("comfortable")}
            aria-pressed={density === "comfortable"}
            style={{
              padding: "6px 10px",
              fontSize: 12,
              background: density === "comfortable" ? "#0f172a" : "#fff",
              color: density === "comfortable" ? "#fff" : "#0f172a",
              border: 0,
              cursor: "pointer",
            }}
          >
            Comfortable
          </button>
          <button
            onClick={() => setDensity("standard")}
            aria-pressed={density === "standard"}
            style={{
              padding: "6px 10px",
              fontSize: 12,
              background: density === "standard" ? "#0f172a" : "#fff",
              color: density === "standard" ? "#fff" : "#0f172a",
              border: 0,
              borderLeft: "1px solid #e5e7eb",
              cursor: "pointer",
            }}
          >
            Standard
          </button>
          <button
            onClick={() => setDensity("compact")}
            aria-pressed={density === "compact"}
            style={{
              padding: "6px 10px",
              fontSize: 12,
              background: density === "compact" ? "#0f172a" : "#fff",
              color: density === "compact" ? "#fff" : "#0f172a",
              border: 0,
              borderLeft: "1px solid #e5e7eb",
              cursor: "pointer",
            }}
          >
            Compact
          </button>
        </div>
      </div>
      <div style={{ display: "inline-flex", gap: 8 }}>
        <button
          onClick={newConsumer}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #0ea5e9",
            background: "#0ea5e9",
            color: "#fff",
            cursor: "pointer",
            fontWeight: 700,
          }}
          title="Create a new Consumer user"
        >
          New Consumer
        </button>
      </div>
      <button
        onClick={handleExport}
        disabled={exporting}
        style={{
          padding: "8px 12px",
          borderRadius: 8,
          border: "1px solid #1d4ed8",
          background: exporting ? "#93c5fd" : "#2563eb",
          color: "#fff",
          cursor: exporting ? "not-allowed" : "pointer",
          fontWeight: 700,
        }}
        title="Download Excel of all users"
      >
        {exporting ? "Exporting..." : "Export Excel"}
      </button>
      <button
        onClick={() => setReloadKey((k) => k + 1)}
        style={{
          padding: "8px 12px",
          borderRadius: 8,
          border: "1px solid #e2e8f0",
          background: "#fff",
          color: "#0f172a",
          cursor: "pointer",
          fontWeight: 600,
        }}
      >
        Refresh
      </button>
    </div>
  );

  const editFieldsWithNames = useMemo(() => {
    const names = {
      country: selected?.country_name || "",
      state: selected?.state_name || "",
      city: selected?.district_name || "",
    };
    const prettify = (name) => {
      if (name === "city") return "District/City";
      return name.charAt(0).toUpperCase() + name.slice(1);
    };
    return (editFields || []).map((f) => {
      if (f && ["country", "state", "city"].includes(f.name)) {
        const baseLabel =
          (f.label ? String(f.label).replace(/\s*\(ID\)\s*$/i, "") : prettify(f.name));
        const help = names[f.name]
          ? `Current: ${names[f.name]}`
          : (f.help_text || "");
        return { ...f, label: baseLabel, help_text: help };
      }
      return f;
    });
  }, [editFields, selected]);

  // Hide password from edit dialog
  const editFieldsNoPassword = useMemo(
    () => (editFieldsWithNames || []).filter((f) => f && String(f.name || "").toLowerCase() !== "password"),
    [editFieldsWithNames]
  );

  // Limit Admin Users edit form to only allowed fields and adapt for State Coordinator
  const editFieldsRestricted = useMemo(() => {
    const allowed = new Set(["email", "full_name", "username", "phone", "sponsor_id", "pincode", "state", "city", "account_active"]);
    const cat = String(selected?.category || "").toLowerCase();
    if (cat === "agency_state_coordinator") {
      allowed.delete("city");
      allowed.delete("pincode");
    }
    if (cat === "merchant") {
      allowed.add("commission_percent");
      allowed.add("service_mode");
    }
    let arr = (editFieldsWithNames || []).filter(
      (f) => f && allowed.has(String(f.name || ""))
    );
    // Ensure Username is present for cascade behavior even if meta didn't include it
    const hasUsername = arr.some((f) => String(f.name) === "username");
    if (!hasUsername) {
      arr = [{ name: "username", type: "CharField", required: false, label: "Username" }, ...arr];
    }
    // Ensure Sponsor ID is present even if meta didn't include it
    const hasSponsor = arr.some((f) => String(f.name) === "sponsor_id");
    if (!hasSponsor) {
      arr = [...arr, { name: "sponsor_id", type: "CharField", required: false, label: "Sponsor ID" }];
    }
    return arr;
  }, [editFieldsWithNames, selected?.category]);

  const editFieldsRestrictedNoPassword = useMemo(
    () => (editFieldsRestricted || []).filter((f) => f && String(f.name || "").toLowerCase() !== "password"),
    [editFieldsRestricted]
  );

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, color: "#0f172a" }}>Community Consumers</h2>
        <div style={{ color: "#64748b", fontSize: 13 }}>
          Community consumers administration table and profiles.
        </div>
      </div>

      {/* Top Academic Term Filter Bar */}
      <div
        style={{
          marginBottom: 14,
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
          boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: "#1e293b", display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span>🎓</span> Academic Term:
          </span>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {[
              { value: "", label: "All Terms" },
              { value: "1", label: "Term 1" },
              { value: "2", label: "Term 2" },
              { value: "3", label: "Term 3" },
              { value: "4", label: "Term 4" },
              { value: "5", label: "Term 5" },
            ].map((t) => {
              const active = (filters.term || "") === t.value;
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setF("term", t.value)}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 8,
                    border: active ? "1px solid #2563eb" : "1px solid #e2e8f0",
                    background: active ? "#2563eb" : "#f8fafc",
                    color: active ? "#ffffff" : "#475569",
                    fontSize: 12,
                    fontWeight: 800,
                    cursor: "pointer",
                    transition: "all 150ms ease",
                    boxShadow: active ? "0 2px 4px rgba(37,99,235,0.2)" : "none",
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>
          Filter active: <span style={{ color: "#2563eb", fontWeight: 800 }}>{(filters.term ? `Term ${filters.term}` : "All Terms")}</span>
        </div>
      </div>

      {/* Filters */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
          gap: 12,
          marginBottom: 12,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 12, color: "#64748b" }}>Data Scope</label>
          <div
            style={{
              padding: "10px 12px",
              borderRadius: 8,
              border: "1px solid #bbf7d0",
              background: "#f0fdf4",
              color: "#166534",
              fontWeight: 800,
              fontSize: 13,
            }}
          >
            Consumers only
          </div>
        </div>
        <Select
          label="Academic Term"
          value={filters.term}
          onChange={(v) => setF("term", v)}
          options={[
            { value: "", label: "All Terms" },
            { value: "1", label: "Term 1" },
            { value: "2", label: "Term 2" },
            { value: "3", label: "Term 3" },
            { value: "4", label: "Term 4" },
            { value: "5", label: "Term 5" },
          ]}
        />
        <Select
          label="State"
          value={filters.state_name}
          onChange={handleStateChange}
          options={stateOptions}
        />
        <Select
          label="District"
          value={filters.district}
          onChange={(v) => setF("district", v)}
          options={districtOptions}
        />
        <TextInput
          label="Phone"
          value={filters.phone}
          onChange={(v) => setF("phone", v)}
          placeholder="digits/contains"
        />
        <TextInput
          label="Pincode"
          value={filters.pincode}
          onChange={(v) => setF("pincode", v)}
          placeholder="exact or contains"
        />
        <Select
          label="Package Tier"
          value={filters.package_tier}
          onChange={(v) => setF("package_tier", v)}
          options={packageTierOptions}
        />
        <Select
          label="Rank / Layer"
          value={filters.rank_layer}
          onChange={(v) => setF("rank_layer", v)}
          options={rankLayerOptions}
        />
        <Select
          label="Activation Mode"
          value={filters.activation_mode}
          onChange={(v) => setF("activation_mode", v)}
          options={activationModeOptions}
        />
        <Select
          label="Direct Sponsors"
          value={filters.directs_range}
          onChange={(v) => setF("directs_range", v)}
          options={directsRangeOptions}
        />
        <Select
          label="KYC Status"
          value={filters.kyc}
          onChange={(v) => setF("kyc", v)}
          options={kycOptions}
        />
        <Select
          label="Account Status"
          value={filters.account_active}
          onChange={(v) => setF("account_active", v)}
          options={accountStatusOptions}
        />
        <Select
          label="Login Access"
          value={filters.is_active}
          onChange={(v) => setF("is_active", v)}
          options={accessStatusOptions}
        />
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={handleResetFilters}
            style={{
              height: 40,
              padding: "0 16px",
              borderRadius: 8,
              border: "1px solid #cbd5e1",
              background: "#ffffff",
              color: "#475569",
              fontSize: 13,
              fontWeight: 800,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
              transition: "all 150ms ease",
            }}
          >
            <span>🔄</span>
            <span>Reset All Filters</span>
          </button>
        </div>
      </div>

      {apiErr ? (
        <div
          style={{
            margin: "8px 0",
            padding: "8px 10px",
            borderRadius: 8,
            background: "#fee2e2",
            color: "#991b1b",
            border: "1px solid #fecaca",
            fontSize: 13,
            fontWeight: 600,
          }}
          title="API error"
        >
          {apiErr}
        </div>
      ) : null}

      <DataTable
        columns={tableColumns}
        fetcher={fetcher}
        density={density}
        toolbar={toolbar}
        initialSortModel={[{ field: "package_buy_date", sort: "asc" }]}
        extraKey={JSON.stringify(filters) + ":" + reloadKey}
        checkboxSelection={true}
        onSelectionChange={() => {}}
        onRowEdit={openUserProfile}
        columnVisibilityModel={colVis}
        onColumnVisibilityModelChange={setColVis}
        instanceKey={location.search || "admin-users"}
      />
      <ModelFormDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        route="/admin/users/"
        record={selected}
        fields={editFieldsRestrictedNoPassword}
        onSaved={() => setReloadKey((k) => k + 1)}
        title={selected?.id ? `Edit ${selected.username || selected.full_name || selected.id}` : "Create User"}
      />

      <AgencyPackageCardsPanel
        open={pkgOpen}
        onClose={() => setPkgOpen(false)}
        agencyId={pkgUser?.id}
        username={pkgUser?.username || pkgUser?.full_name}
      />
      <UserProfileDrawer
        open={profileOpen}
        loading={profileLoading}
        user={profileUser}
        initialTab={drawerInitialTab}
        onClose={() => setProfileOpen(false)}
        onEdit={() => {
          const row = profileUser;
          setProfileOpen(false);
          openEdit(row);
        }}
        onReset={() => setReloadKey((k) => k + 1)}
      />
      {directsModalUser && (
        <DirectsModal
          user={directsModalUser}
          onClose={() => setDirectsModalUser(null)}
        />
      )}
    </div>
  );
}
